async function createGeneratedRouteFactory(app, routeDef, routeName, moduleLoader, modelLoader) {
    const { default: fallbackModuleClass, ...moduleExports } = await moduleLoader();
    const { default: fallbackModelClass, ...modelExports } = await modelLoader();
    const authManager = app.authManager;
    if (!authManager)
        throw new Error(`Application is missing "authManager" required for generated route "${routeName}".`);

    const ModuleClass = fallbackModuleClass || Object.values(moduleExports)[0];
    const ModelClass = fallbackModelClass || Object.values(modelExports)[0];
    const dataModel = ModelClass.getInstance(authManager);
    if (typeof app.prepareDataModel === 'function')
        await app.prepareDataModel(dataModel);
    else
        await dataModel.prepare();

    return new ModuleClass(null, {
        ...routeDef.options,
        app,
        dataModel,
    });
}

export const generatedRoutes = [
    {
        name: 'default-core-setting-browser',
        path: '/default/core/setting',
        privileges: null,
        options: {
            "entityName": "core.Setting",
            "name": "default-core-setting-browser",
            "moduleName": "default-core-setting-browser",
            "moduleTitle": "Settings",
            "moduleStateSlotName": "default-core-setting-browser"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-core-setting-browser',
            () => import('../lib/ui/modules/DataBrowser.js'),
            () => import('./modules/core/models/SettingModel.js'),
        ),
    },
    {
        name: 'default-core-setting-editor',
        path: '/default/core/setting/:id',
        privileges: null,
        options: {
            "entityName": "core.Setting",
            "name": "default-core-setting-editor",
            "moduleName": "default-core-setting-editor",
            "moduleTitle": "Setting",
            "moduleStateSlotName": "default-core-setting-editor"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-core-setting-editor',
            () => import('../lib/ui/modules/DataEditor.js'),
            () => import('./modules/core/models/SettingModel.js'),
        ),
    },
    {
        name: 'default-core-timezone-browser',
        path: '/default/core/timezone',
        privileges: [["adminSystem"]],
        options: {
            "entityName": "core.TimeZone",
            "name": "default-core-timezone-browser",
            "moduleName": "default-core-timezone-browser",
            "moduleTitle": "Time Zones",
            "moduleStateSlotName": "default-core-timezone-browser"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-core-timezone-browser',
            () => import('../lib/ui/modules/DataBrowser.js'),
            () => import('./modules/core/models/TimeZoneModel.js'),
        ),
    },
    {
        name: 'default-core-timezone-editor',
        path: '/default/core/timezone/:id',
        privileges: [["adminSystem"]],
        options: {
            "entityName": "core.TimeZone",
            "name": "default-core-timezone-editor",
            "moduleName": "default-core-timezone-editor",
            "moduleTitle": "Time Zone",
            "moduleStateSlotName": "default-core-timezone-editor"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-core-timezone-editor',
            () => import('../lib/ui/modules/DataEditor.js'),
            () => import('./modules/core/models/TimeZoneModel.js'),
        ),
    },
    {
        name: 'default-core-locale-browser',
        path: '/default/core/locale',
        privileges: [["adminSystem"]],
        options: {
            "entityName": "core.Locale",
            "name": "default-core-locale-browser",
            "moduleName": "default-core-locale-browser",
            "moduleTitle": "Locales",
            "moduleStateSlotName": "default-core-locale-browser"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-core-locale-browser',
            () => import('../lib/ui/modules/DataBrowser.js'),
            () => import('./modules/core/models/LocaleModel.js'),
        ),
    },
    {
        name: 'default-core-locale-editor',
        path: '/default/core/locale/:id',
        privileges: [["adminSystem"]],
        options: {
            "entityName": "core.Locale",
            "name": "default-core-locale-editor",
            "moduleName": "default-core-locale-editor",
            "moduleTitle": "Locale",
            "moduleStateSlotName": "default-core-locale-editor"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-core-locale-editor',
            () => import('../lib/ui/modules/DataEditor.js'),
            () => import('./modules/core/models/LocaleModel.js'),
        ),
    },
    {
        name: 'default-core-user-browser',
        path: '/default/core/user',
        privileges: [["adminUsers"],["manageUsers"]],
        options: {
            "entityName": "core.User",
            "name": "default-core-user-browser",
            "moduleName": "default-core-user-browser",
            "moduleTitle": "Users",
            "moduleStateSlotName": "default-core-user-browser"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-core-user-browser',
            () => import('../lib/ui/modules/DataBrowser.js'),
            () => import('./modules/core/models/UserModel.js'),
        ),
    },
    {
        name: 'default-core-user-editor',
        path: '/default/core/user/:id',
        privileges: [["adminUsers"],["manageUsers"]],
        options: {
            "entityName": "core.User",
            "name": "default-core-user-editor",
            "moduleName": "default-core-user-editor",
            "moduleTitle": "User",
            "moduleStateSlotName": "default-core-user-editor"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-core-user-editor',
            () => import('../lib/ui/modules/DataEditor.js'),
            () => import('./modules/core/models/UserModel.js'),
        ),
    },
    {
        name: 'default-core-privilege-browser',
        path: '/default/core/privilege',
        privileges: [["adminUsers"]],
        options: {
            "entityName": "core.Privilege",
            "name": "default-core-privilege-browser",
            "moduleName": "default-core-privilege-browser",
            "moduleTitle": "Privileges",
            "moduleStateSlotName": "default-core-privilege-browser"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-core-privilege-browser',
            () => import('../lib/ui/modules/DataBrowser.js'),
            () => import('./modules/core/models/PrivilegeModel.js'),
        ),
    },
    {
        name: 'default-core-privilege-editor',
        path: '/default/core/privilege/:id',
        privileges: [["adminUsers"]],
        options: {
            "entityName": "core.Privilege",
            "name": "default-core-privilege-editor",
            "moduleName": "default-core-privilege-editor",
            "moduleTitle": "Privilege",
            "moduleStateSlotName": "default-core-privilege-editor"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-core-privilege-editor',
            () => import('../lib/ui/modules/DataEditor.js'),
            () => import('./modules/core/models/PrivilegeModel.js'),
        ),
    },
    {
        name: 'default-core-role-browser',
        path: '/default/core/role',
        privileges: [["adminUsers"]],
        options: {
            "entityName": "core.Role",
            "name": "default-core-role-browser",
            "moduleName": "default-core-role-browser",
            "moduleTitle": "Roles",
            "moduleStateSlotName": "default-core-role-browser"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-core-role-browser',
            () => import('../lib/ui/modules/DataBrowser.js'),
            () => import('./modules/core/models/RoleModel.js'),
        ),
    },
    {
        name: 'default-core-role-editor',
        path: '/default/core/role/:id',
        privileges: [["adminUsers"]],
        options: {
            "entityName": "core.Role",
            "name": "default-core-role-editor",
            "moduleName": "default-core-role-editor",
            "moduleTitle": "Role",
            "moduleStateSlotName": "default-core-role-editor"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-core-role-editor',
            () => import('../lib/ui/modules/DataEditor.js'),
            () => import('./modules/core/models/RoleModel.js'),
        ),
    },
    {
        name: 'default-core-roleprivilege-browser',
        path: '/default/core/roleprivilege',
        privileges: [["adminUsers"]],
        options: {
            "entityName": "core.RolePrivilege",
            "name": "default-core-roleprivilege-browser",
            "moduleName": "default-core-roleprivilege-browser",
            "moduleTitle": "Role Privileges",
            "moduleStateSlotName": "default-core-roleprivilege-browser"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-core-roleprivilege-browser',
            () => import('../lib/ui/modules/DataBrowser.js'),
            () => import('./modules/core/models/RolePrivilegeModel.js'),
        ),
    },
    {
        name: 'default-core-roleprivilege-editor',
        path: '/default/core/roleprivilege/:id',
        privileges: [["adminUsers"]],
        options: {
            "entityName": "core.RolePrivilege",
            "name": "default-core-roleprivilege-editor",
            "moduleName": "default-core-roleprivilege-editor",
            "moduleTitle": "Role Privilege",
            "moduleStateSlotName": "default-core-roleprivilege-editor"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-core-roleprivilege-editor',
            () => import('../lib/ui/modules/DataEditor.js'),
            () => import('./modules/core/models/RolePrivilegeModel.js'),
        ),
    },
    {
        name: 'default-core-userrole-browser',
        path: '/default/core/userrole',
        privileges: [["adminUsers"]],
        options: {
            "entityName": "core.UserRole",
            "name": "default-core-userrole-browser",
            "moduleName": "default-core-userrole-browser",
            "moduleTitle": "User Roles",
            "moduleStateSlotName": "default-core-userrole-browser"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-core-userrole-browser',
            () => import('../lib/ui/modules/DataBrowser.js'),
            () => import('./modules/core/models/UserRoleModel.js'),
        ),
    },
    {
        name: 'default-core-userrole-editor',
        path: '/default/core/userrole/:id',
        privileges: [["adminUsers"]],
        options: {
            "entityName": "core.UserRole",
            "name": "default-core-userrole-editor",
            "moduleName": "default-core-userrole-editor",
            "moduleTitle": "User Role",
            "moduleStateSlotName": "default-core-userrole-editor"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-core-userrole-editor',
            () => import('../lib/ui/modules/DataEditor.js'),
            () => import('./modules/core/models/UserRoleModel.js'),
        ),
    },
    {
        name: 'default-core-permission-browser',
        path: '/default/core/permission',
        privileges: null,
        options: {
            "entityName": "core.Permission",
            "name": "default-core-permission-browser",
            "moduleName": "default-core-permission-browser",
            "moduleTitle": "Privileges",
            "moduleStateSlotName": "default-core-permission-browser"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-core-permission-browser',
            () => import('../lib/ui/modules/DataBrowser.js'),
            () => import('./modules/core/models/PermissionModel.js'),
        ),
    },
    {
        name: 'default-core-permission-editor',
        path: '/default/core/permission/:id',
        privileges: null,
        options: {
            "entityName": "core.Permission",
            "name": "default-core-permission-editor",
            "moduleName": "default-core-permission-editor",
            "moduleTitle": "Privilege",
            "moduleStateSlotName": "default-core-permission-editor"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-core-permission-editor',
            () => import('../lib/ui/modules/DataEditor.js'),
            () => import('./modules/core/models/PermissionModel.js'),
        ),
    },
    {
        name: 'default-core-foldertype-browser',
        path: '/default/core/foldertype',
        privileges: null,
        options: {
            "entityName": "core.FolderType",
            "name": "default-core-foldertype-browser",
            "moduleName": "default-core-foldertype-browser",
            "moduleTitle": "Folder Types",
            "moduleStateSlotName": "default-core-foldertype-browser"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-core-foldertype-browser',
            () => import('../lib/ui/modules/DataBrowser.js'),
            () => import('./modules/core/models/FolderTypeModel.js'),
        ),
    },
    {
        name: 'default-core-foldertype-editor',
        path: '/default/core/foldertype/:id',
        privileges: null,
        options: {
            "entityName": "core.FolderType",
            "name": "default-core-foldertype-editor",
            "moduleName": "default-core-foldertype-editor",
            "moduleTitle": "Folder Type",
            "moduleStateSlotName": "default-core-foldertype-editor"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-core-foldertype-editor',
            () => import('../lib/ui/modules/DataEditor.js'),
            () => import('./modules/core/models/FolderTypeModel.js'),
        ),
    },
    {
        name: 'default-core-folder-browser',
        path: '/default/core/folder',
        privileges: [["adminFileStorage"],["useFileStorage"]],
        options: {
            "entityName": "core.Folder",
            "name": "default-core-folder-browser",
            "moduleName": "default-core-folder-browser",
            "moduleTitle": "Folders",
            "moduleStateSlotName": "default-core-folder-browser"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-core-folder-browser',
            () => import('../lib/ui/modules/DataBrowser.js'),
            () => import('./modules/core/models/FolderModel.js'),
        ),
    },
    {
        name: 'default-core-folder-editor',
        path: '/default/core/folder/:id',
        privileges: [["adminFileStorage"],["useFileStorage"]],
        options: {
            "entityName": "core.Folder",
            "name": "default-core-folder-editor",
            "moduleName": "default-core-folder-editor",
            "moduleTitle": "Folder",
            "moduleStateSlotName": "default-core-folder-editor"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-core-folder-editor',
            () => import('../lib/ui/modules/DataEditor.js'),
            () => import('./modules/core/models/FolderModel.js'),
        ),
    },
    {
        name: 'default-core-folderpermission-browser',
        path: '/default/core/folderpermission',
        privileges: [["adminFileStorage"],["useFileStorage"]],
        options: {
            "entityName": "core.FolderPermission",
            "name": "default-core-folderpermission-browser",
            "moduleName": "default-core-folderpermission-browser",
            "moduleTitle": "Folder Permissions",
            "moduleStateSlotName": "default-core-folderpermission-browser"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-core-folderpermission-browser',
            () => import('../lib/ui/modules/DataBrowser.js'),
            () => import('./modules/core/models/FolderPermissionModel.js'),
        ),
    },
    {
        name: 'default-core-folderpermission-editor',
        path: '/default/core/folderpermission/:id',
        privileges: [["adminFileStorage"],["useFileStorage"]],
        options: {
            "entityName": "core.FolderPermission",
            "name": "default-core-folderpermission-editor",
            "moduleName": "default-core-folderpermission-editor",
            "moduleTitle": "Folder Permission",
            "moduleStateSlotName": "default-core-folderpermission-editor"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-core-folderpermission-editor',
            () => import('../lib/ui/modules/DataEditor.js'),
            () => import('./modules/core/models/FolderPermissionModel.js'),
        ),
    },
    {
        name: 'default-core-filetype-browser',
        path: '/default/core/filetype',
        privileges: null,
        options: {
            "entityName": "core.FileType",
            "name": "default-core-filetype-browser",
            "moduleName": "default-core-filetype-browser",
            "moduleTitle": "File Types",
            "moduleStateSlotName": "default-core-filetype-browser"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-core-filetype-browser',
            () => import('../lib/ui/modules/DataBrowser.js'),
            () => import('./modules/core/models/FileTypeModel.js'),
        ),
    },
    {
        name: 'default-core-filetype-editor',
        path: '/default/core/filetype/:id',
        privileges: null,
        options: {
            "entityName": "core.FileType",
            "name": "default-core-filetype-editor",
            "moduleName": "default-core-filetype-editor",
            "moduleTitle": "File Type",
            "moduleStateSlotName": "default-core-filetype-editor"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-core-filetype-editor',
            () => import('../lib/ui/modules/DataEditor.js'),
            () => import('./modules/core/models/FileTypeModel.js'),
        ),
    },
    {
        name: 'default-core-file-browser',
        path: '/default/core/file',
        privileges: [["adminFileStorage"],["useFileStorage"]],
        options: {
            "entityName": "core.File",
            "name": "default-core-file-browser",
            "moduleName": "default-core-file-browser",
            "moduleTitle": "Soubory",
            "moduleStateSlotName": "default-core-file-browser"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-core-file-browser',
            () => import('../lib/ui/modules/DataBrowser.js'),
            () => import('./modules/core/models/FileModel.js'),
        ),
    },
    {
        name: 'default-core-file-editor',
        path: '/default/core/file/:id',
        privileges: [["adminFileStorage"],["useFileStorage"]],
        options: {
            "entityName": "core.File",
            "name": "default-core-file-editor",
            "moduleName": "default-core-file-editor",
            "moduleTitle": "Soubor",
            "moduleStateSlotName": "default-core-file-editor"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-core-file-editor',
            () => import('../lib/ui/modules/DataEditor.js'),
            () => import('./modules/core/models/FileModel.js'),
        ),
    },
    {
        name: 'default-core-workflow-browser',
        path: '/default/core/workflow',
        privileges: [["adminWorkflow"]],
        options: {
            "entityName": "core.Workflow",
            "name": "default-core-workflow-browser",
            "moduleName": "default-core-workflow-browser",
            "moduleTitle": "Workflow",
            "moduleStateSlotName": "default-core-workflow-browser"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-core-workflow-browser',
            () => import('../lib/ui/modules/DataBrowser.js'),
            () => import('./modules/core/models/WorkflowModel.js'),
        ),
    },
    {
        name: 'default-core-workflow-editor',
        path: '/default/core/workflow/:id',
        privileges: [["adminWorkflow"]],
        options: {
            "entityName": "core.Workflow",
            "name": "default-core-workflow-editor",
            "moduleName": "default-core-workflow-editor",
            "moduleTitle": "Workflow",
            "moduleStateSlotName": "default-core-workflow-editor"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-core-workflow-editor',
            () => import('../lib/ui/modules/DataEditor.js'),
            () => import('./modules/core/models/WorkflowModel.js'),
        ),
    },
    {
        name: 'default-core-workflowstatus-browser',
        path: '/default/core/workflowstatus',
        privileges: [["adminWorkflow"]],
        options: {
            "entityName": "core.WorkflowStatus",
            "name": "default-core-workflowstatus-browser",
            "moduleName": "default-core-workflowstatus-browser",
            "moduleTitle": "Workflow States",
            "moduleStateSlotName": "default-core-workflowstatus-browser"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-core-workflowstatus-browser',
            () => import('../lib/ui/modules/DataBrowser.js'),
            () => import('./modules/core/models/WorkflowStatusModel.js'),
        ),
    },
    {
        name: 'default-core-workflowstatus-editor',
        path: '/default/core/workflowstatus/:id',
        privileges: [["adminWorkflow"]],
        options: {
            "entityName": "core.WorkflowStatus",
            "name": "default-core-workflowstatus-editor",
            "moduleName": "default-core-workflowstatus-editor",
            "moduleTitle": "Workflow State",
            "moduleStateSlotName": "default-core-workflowstatus-editor"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-core-workflowstatus-editor',
            () => import('../lib/ui/modules/DataEditor.js'),
            () => import('./modules/core/models/WorkflowStatusModel.js'),
        ),
    },
    {
        name: 'default-core-workflowtransition-browser',
        path: '/default/core/workflowtransition',
        privileges: [["adminWorkflow"]],
        options: {
            "entityName": "core.WorkflowTransition",
            "name": "default-core-workflowtransition-browser",
            "moduleName": "default-core-workflowtransition-browser",
            "moduleTitle": "Workflow Transitions",
            "moduleStateSlotName": "default-core-workflowtransition-browser"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-core-workflowtransition-browser',
            () => import('../lib/ui/modules/DataBrowser.js'),
            () => import('./modules/core/models/WorkflowTransitionModel.js'),
        ),
    },
    {
        name: 'default-core-workflowtransition-editor',
        path: '/default/core/workflowtransition/:id',
        privileges: [["adminWorkflow"]],
        options: {
            "entityName": "core.WorkflowTransition",
            "name": "default-core-workflowtransition-editor",
            "moduleName": "default-core-workflowtransition-editor",
            "moduleTitle": "Workflow Transition",
            "moduleStateSlotName": "default-core-workflowtransition-editor"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-core-workflowtransition-editor',
            () => import('../lib/ui/modules/DataEditor.js'),
            () => import('./modules/core/models/WorkflowTransitionModel.js'),
        ),
    },
    {
        name: 'default-core-workflowtransitionprivilege-browser',
        path: '/default/core/workflowtransitionprivilege',
        privileges: [["adminWorkflow"]],
        options: {
            "entityName": "core.WorkflowTransitionPrivilege",
            "name": "default-core-workflowtransitionprivilege-browser",
            "moduleName": "default-core-workflowtransitionprivilege-browser",
            "moduleTitle": "Workflow Transition Privileges",
            "moduleStateSlotName": "default-core-workflowtransitionprivilege-browser"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-core-workflowtransitionprivilege-browser',
            () => import('../lib/ui/modules/DataBrowser.js'),
            () => import('./modules/core/models/WorkflowTransitionPrivilegeModel.js'),
        ),
    },
    {
        name: 'default-core-workflowtransitionprivilege-editor',
        path: '/default/core/workflowtransitionprivilege/:id',
        privileges: [["adminWorkflow"]],
        options: {
            "entityName": "core.WorkflowTransitionPrivilege",
            "name": "default-core-workflowtransitionprivilege-editor",
            "moduleName": "default-core-workflowtransitionprivilege-editor",
            "moduleTitle": "Workflow Transition Privilege",
            "moduleStateSlotName": "default-core-workflowtransitionprivilege-editor"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-core-workflowtransitionprivilege-editor',
            () => import('../lib/ui/modules/DataEditor.js'),
            () => import('./modules/core/models/WorkflowTransitionPrivilegeModel.js'),
        ),
    },
    {
        name: 'default-measurements-quantity-browser',
        path: '/default/measurements/quantity',
        privileges: null,
        options: {
            "entityName": "measurements.Quantity",
            "name": "default-measurements-quantity-browser",
            "moduleName": "default-measurements-quantity-browser",
            "moduleTitle": "Measurement Quantities",
            "moduleStateSlotName": "default-measurements-quantity-browser"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-measurements-quantity-browser',
            () => import('../lib/ui/modules/DataBrowser.js'),
            () => import('./modules/measurements/models/QuantityModel.js'),
        ),
    },
    {
        name: 'default-measurements-quantity-editor',
        path: '/default/measurements/quantity/:id',
        privileges: null,
        options: {
            "entityName": "measurements.Quantity",
            "name": "default-measurements-quantity-editor",
            "moduleName": "default-measurements-quantity-editor",
            "moduleTitle": "Measurement Quantity",
            "moduleStateSlotName": "default-measurements-quantity-editor"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-measurements-quantity-editor',
            () => import('../lib/ui/modules/DataEditor.js'),
            () => import('./modules/measurements/models/QuantityModel.js'),
        ),
    },
    {
        name: 'default-measurements-unit-browser',
        path: '/default/measurements/unit',
        privileges: null,
        options: {
            "entityName": "measurements.Unit",
            "name": "default-measurements-unit-browser",
            "moduleName": "default-measurements-unit-browser",
            "moduleTitle": "Measurement Units",
            "moduleStateSlotName": "default-measurements-unit-browser"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-measurements-unit-browser',
            () => import('../lib/ui/modules/DataBrowser.js'),
            () => import('./modules/measurements/models/UnitModel.js'),
        ),
    },
    {
        name: 'default-measurements-unit-editor',
        path: '/default/measurements/unit/:id',
        privileges: null,
        options: {
            "entityName": "measurements.Unit",
            "name": "default-measurements-unit-editor",
            "moduleName": "default-measurements-unit-editor",
            "moduleTitle": "Measurement Unit",
            "moduleStateSlotName": "default-measurements-unit-editor"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-measurements-unit-editor',
            () => import('../lib/ui/modules/DataEditor.js'),
            () => import('./modules/measurements/models/UnitModel.js'),
        ),
    },
    {
        name: 'default-projects-projecttype-browser',
        path: '/default/projects/projecttype',
        privileges: [["adminProjects"]],
        options: {
            "entityName": "projects.ProjectType",
            "name": "default-projects-projecttype-browser",
            "moduleName": "default-projects-projecttype-browser",
            "moduleTitle": "Project Types",
            "moduleStateSlotName": "default-projects-projecttype-browser"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-projects-projecttype-browser',
            () => import('../lib/ui/modules/DataBrowser.js'),
            () => import('./modules/projects/models/ProjectTypeModel.js'),
        ),
    },
    {
        name: 'default-projects-projecttype-editor',
        path: '/default/projects/projecttype/:id',
        privileges: [["adminProjects"]],
        options: {
            "entityName": "projects.ProjectType",
            "name": "default-projects-projecttype-editor",
            "moduleName": "default-projects-projecttype-editor",
            "moduleTitle": "Project Type",
            "moduleStateSlotName": "default-projects-projecttype-editor"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-projects-projecttype-editor',
            () => import('../lib/ui/modules/DataEditor.js'),
            () => import('./modules/projects/models/ProjectTypeModel.js'),
        ),
    },
    {
        name: 'default-projects-project-browser',
        path: '/default/projects/project',
        privileges: [["adminProjects"],["manageProjects"]],
        options: {
            "entityName": "projects.Project",
            "name": "default-projects-project-browser",
            "moduleName": "default-projects-project-browser",
            "moduleTitle": "Projects",
            "moduleStateSlotName": "default-projects-project-browser"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-projects-project-browser',
            () => import('../lib/ui/modules/DataBrowser.js'),
            () => import('./modules/projects/models/ProjectModel.js'),
        ),
    },
    {
        name: 'default-projects-project-editor',
        path: '/default/projects/project/:id',
        privileges: [["adminProjects"],["manageProjects"]],
        options: {
            "entityName": "projects.Project",
            "name": "default-projects-project-editor",
            "moduleName": "default-projects-project-editor",
            "moduleTitle": "Project",
            "moduleStateSlotName": "default-projects-project-editor"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-projects-project-editor',
            () => import('../lib/ui/modules/DataEditor.js'),
            () => import('./modules/projects/models/ProjectModel.js'),
        ),
    },
    {
        name: 'default-projects-projectstagetype-browser',
        path: '/default/projects/projectstagetype',
        privileges: [["adminProjects"]],
        options: {
            "entityName": "projects.ProjectStageType",
            "name": "default-projects-projectstagetype-browser",
            "moduleName": "default-projects-projectstagetype-browser",
            "moduleTitle": "Project Stage Types",
            "moduleStateSlotName": "default-projects-projectstagetype-browser"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-projects-projectstagetype-browser',
            () => import('../lib/ui/modules/DataBrowser.js'),
            () => import('./modules/projects/models/ProjectStageTypeModel.js'),
        ),
    },
    {
        name: 'default-projects-projectstagetype-editor',
        path: '/default/projects/projectstagetype/:id',
        privileges: [["adminProjects"]],
        options: {
            "entityName": "projects.ProjectStageType",
            "name": "default-projects-projectstagetype-editor",
            "moduleName": "default-projects-projectstagetype-editor",
            "moduleTitle": "Project Stage Type",
            "moduleStateSlotName": "default-projects-projectstagetype-editor"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-projects-projectstagetype-editor',
            () => import('../lib/ui/modules/DataEditor.js'),
            () => import('./modules/projects/models/ProjectStageTypeModel.js'),
        ),
    },
    {
        name: 'default-projects-workspace-browser',
        path: '/default/projects/workspace',
        privileges: [["adminWorkspace"],["manageWorkspace"]],
        options: {
            "entityName": "projects.Workspace",
            "name": "default-projects-workspace-browser",
            "moduleName": "default-projects-workspace-browser",
            "moduleTitle": "Workspaces",
            "moduleStateSlotName": "default-projects-workspace-browser"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-projects-workspace-browser',
            () => import('../lib/ui/modules/DataBrowser.js'),
            () => import('./modules/projects/models/WorkspaceModel.js'),
        ),
    },
    {
        name: 'default-projects-workspace-editor',
        path: '/default/projects/workspace/:id',
        privileges: [["adminWorkspace"],["manageWorkspace"]],
        options: {
            "entityName": "projects.Workspace",
            "name": "default-projects-workspace-editor",
            "moduleName": "default-projects-workspace-editor",
            "moduleTitle": "Workspace",
            "moduleStateSlotName": "default-projects-workspace-editor"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-projects-workspace-editor',
            () => import('../lib/ui/modules/DataEditor.js'),
            () => import('./modules/projects/models/WorkspaceModel.js'),
        ),
    },
    {
        name: 'default-projects-workspacerole-browser',
        path: '/default/projects/workspacerole',
        privileges: [["adminWorkspace"],["manageWorkspace"]],
        options: {
            "entityName": "projects.WorkspaceRole",
            "name": "default-projects-workspacerole-browser",
            "moduleName": "default-projects-workspacerole-browser",
            "moduleTitle": "Workspaces",
            "moduleStateSlotName": "default-projects-workspacerole-browser"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-projects-workspacerole-browser',
            () => import('../lib/ui/modules/DataBrowser.js'),
            () => import('./modules/projects/models/WorkspaceRoleModel.js'),
        ),
    },
    {
        name: 'default-projects-workspacerole-editor',
        path: '/default/projects/workspacerole/:id',
        privileges: [["adminWorkspace"],["manageWorkspace"]],
        options: {
            "entityName": "projects.WorkspaceRole",
            "name": "default-projects-workspacerole-editor",
            "moduleName": "default-projects-workspacerole-editor",
            "moduleTitle": "Workspace Role",
            "moduleStateSlotName": "default-projects-workspacerole-editor"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-projects-workspacerole-editor',
            () => import('../lib/ui/modules/DataEditor.js'),
            () => import('./modules/projects/models/WorkspaceRoleModel.js'),
        ),
    },
    {
        name: 'default-projects-workspaceproject-browser',
        path: '/default/projects/workspaceproject',
        privileges: [["adminWorkspace"],["manageWorkspace"]],
        options: {
            "entityName": "projects.WorkspaceProject",
            "name": "default-projects-workspaceproject-browser",
            "moduleName": "default-projects-workspaceproject-browser",
            "moduleTitle": "Workspace Projects",
            "moduleStateSlotName": "default-projects-workspaceproject-browser"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-projects-workspaceproject-browser',
            () => import('../lib/ui/modules/DataBrowser.js'),
            () => import('./modules/projects/models/WorkspaceProjectModel.js'),
        ),
    },
    {
        name: 'default-projects-workspaceproject-editor',
        path: '/default/projects/workspaceproject/:id',
        privileges: [["adminWorkspace"],["manageWorkspace"]],
        options: {
            "entityName": "projects.WorkspaceProject",
            "name": "default-projects-workspaceproject-editor",
            "moduleName": "default-projects-workspaceproject-editor",
            "moduleTitle": "Workspace Project",
            "moduleStateSlotName": "default-projects-workspaceproject-editor"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-projects-workspaceproject-editor',
            () => import('../lib/ui/modules/DataEditor.js'),
            () => import('./modules/projects/models/WorkspaceProjectModel.js'),
        ),
    },
    {
        name: 'default-standards-codestandarddocumenttype-browser',
        path: '/default/standards/codestandarddocumenttype',
        privileges: [["adminStandards"]],
        options: {
            "entityName": "standards.CodeStandardDocumentType",
            "name": "default-standards-codestandarddocumenttype-browser",
            "moduleName": "default-standards-codestandarddocumenttype-browser",
            "moduleTitle": "Cds Types",
            "moduleStateSlotName": "default-standards-codestandarddocumenttype-browser"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-standards-codestandarddocumenttype-browser',
            () => import('../lib/ui/modules/DataBrowser.js'),
            () => import('./modules/standards/models/CodeStandardDocumentTypeModel.js'),
        ),
    },
    {
        name: 'default-standards-codestandarddocumenttype-editor',
        path: '/default/standards/codestandarddocumenttype/:id',
        privileges: [["adminStandards"]],
        options: {
            "entityName": "standards.CodeStandardDocumentType",
            "name": "default-standards-codestandarddocumenttype-editor",
            "moduleName": "default-standards-codestandarddocumenttype-editor",
            "moduleTitle": "Cds Type",
            "moduleStateSlotName": "default-standards-codestandarddocumenttype-editor"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-standards-codestandarddocumenttype-editor',
            () => import('../lib/ui/modules/DataEditor.js'),
            () => import('./modules/standards/models/CodeStandardDocumentTypeModel.js'),
        ),
    },
    {
        name: 'default-standards-codestandarddocument-browser',
        path: '/default/standards/codestandarddocument',
        privileges: [["adminStandards"]],
        options: {
            "entityName": "standards.CodeStandardDocument",
            "name": "default-standards-codestandarddocument-browser",
            "moduleName": "default-standards-codestandarddocument-browser",
            "moduleTitle": "Code Standard Documents",
            "moduleStateSlotName": "default-standards-codestandarddocument-browser"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-standards-codestandarddocument-browser',
            () => import('../lib/ui/modules/DataBrowser.js'),
            () => import('./modules/standards/models/CodeStandardDocumentModel.js'),
        ),
    },
    {
        name: 'default-standards-codestandarddocument-editor',
        path: '/default/standards/codestandarddocument/:id',
        privileges: [["adminStandards"]],
        options: {
            "entityName": "standards.CodeStandardDocument",
            "name": "default-standards-codestandarddocument-editor",
            "moduleName": "default-standards-codestandarddocument-editor",
            "moduleTitle": "Code Standard Document",
            "moduleStateSlotName": "default-standards-codestandarddocument-editor"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-standards-codestandarddocument-editor',
            () => import('../lib/ui/modules/DataEditor.js'),
            () => import('./modules/standards/models/CodeStandardDocumentModel.js'),
        ),
    },
    {
        name: 'default-sites-constructionmethod-browser',
        path: '/default/sites/constructionmethod',
        privileges: [["adminSites"]],
        options: {
            "entityName": "sites.ConstructionMethod",
            "name": "default-sites-constructionmethod-browser",
            "moduleName": "default-sites-constructionmethod-browser",
            "moduleTitle": "Construction methods",
            "moduleStateSlotName": "default-sites-constructionmethod-browser"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-sites-constructionmethod-browser',
            () => import('../lib/ui/modules/DataBrowser.js'),
            () => import('./modules/sites/models/ConstructionMethodModel.js'),
        ),
    },
    {
        name: 'default-sites-constructionmethod-editor',
        path: '/default/sites/constructionmethod/:id',
        privileges: [["adminSites"]],
        options: {
            "entityName": "sites.ConstructionMethod",
            "name": "default-sites-constructionmethod-editor",
            "moduleName": "default-sites-constructionmethod-editor",
            "moduleTitle": "Construction method",
            "moduleStateSlotName": "default-sites-constructionmethod-editor"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-sites-constructionmethod-editor',
            () => import('../lib/ui/modules/DataEditor.js'),
            () => import('./modules/sites/models/ConstructionMethodModel.js'),
        ),
    },
    {
        name: 'default-sites-sitetype-browser',
        path: '/default/sites/sitetype',
        privileges: [["adminSites"]],
        options: {
            "entityName": "sites.SiteType",
            "name": "default-sites-sitetype-browser",
            "moduleName": "default-sites-sitetype-browser",
            "moduleTitle": "Site Types",
            "moduleStateSlotName": "default-sites-sitetype-browser"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-sites-sitetype-browser',
            () => import('../lib/ui/modules/DataBrowser.js'),
            () => import('./modules/sites/models/SiteTypeModel.js'),
        ),
    },
    {
        name: 'default-sites-sitetype-editor',
        path: '/default/sites/sitetype/:id',
        privileges: [["adminSites"]],
        options: {
            "entityName": "sites.SiteType",
            "name": "default-sites-sitetype-editor",
            "moduleName": "default-sites-sitetype-editor",
            "moduleTitle": "Site Type",
            "moduleStateSlotName": "default-sites-sitetype-editor"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-sites-sitetype-editor',
            () => import('../lib/ui/modules/DataEditor.js'),
            () => import('./modules/sites/models/SiteTypeModel.js'),
        ),
    },
    {
        name: 'default-sites-site-browser',
        path: '/default/sites/site',
        privileges: [["adminSites"]],
        options: {
            "entityName": "sites.Site",
            "name": "default-sites-site-browser",
            "moduleName": "default-sites-site-browser",
            "moduleTitle": "Sites",
            "moduleStateSlotName": "default-sites-site-browser"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-sites-site-browser',
            () => import('../lib/ui/modules/DataBrowser.js'),
            () => import('./modules/sites/models/SiteModel.js'),
        ),
    },
    {
        name: 'default-sites-site-editor',
        path: '/default/sites/site/:id',
        privileges: [["adminSites"]],
        options: {
            "entityName": "sites.Site",
            "name": "default-sites-site-editor",
            "moduleName": "default-sites-site-editor",
            "moduleTitle": "Site",
            "moduleStateSlotName": "default-sites-site-editor"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-sites-site-editor',
            () => import('../lib/ui/modules/DataEditor.js'),
            () => import('./modules/sites/models/SiteModel.js'),
        ),
    },
    {
        name: 'default-sites-locationtype-browser',
        path: '/default/sites/locationtype',
        privileges: [["adminSites"]],
        options: {
            "entityName": "sites.LocationType",
            "name": "default-sites-locationtype-browser",
            "moduleName": "default-sites-locationtype-browser",
            "moduleTitle": "Location Types",
            "moduleStateSlotName": "default-sites-locationtype-browser"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-sites-locationtype-browser',
            () => import('../lib/ui/modules/DataBrowser.js'),
            () => import('./modules/sites/models/LocationTypeModel.js'),
        ),
    },
    {
        name: 'default-sites-locationtype-editor',
        path: '/default/sites/locationtype/:id',
        privileges: [["adminSites"]],
        options: {
            "entityName": "sites.LocationType",
            "name": "default-sites-locationtype-editor",
            "moduleName": "default-sites-locationtype-editor",
            "moduleTitle": "Location Type",
            "moduleStateSlotName": "default-sites-locationtype-editor"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-sites-locationtype-editor',
            () => import('../lib/ui/modules/DataEditor.js'),
            () => import('./modules/sites/models/LocationTypeModel.js'),
        ),
    },
    {
        name: 'default-sites-location-browser',
        path: '/default/sites/location',
        privileges: [["adminSites"]],
        options: {
            "entityName": "sites.Location",
            "name": "default-sites-location-browser",
            "moduleName": "default-sites-location-browser",
            "moduleTitle": "Locations (Objects)",
            "moduleStateSlotName": "default-sites-location-browser"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-sites-location-browser',
            () => import('../lib/ui/modules/DataBrowser.js'),
            () => import('./modules/sites/models/LocationModel.js'),
        ),
    },
    {
        name: 'default-sites-location-editor',
        path: '/default/sites/location/:id',
        privileges: [["adminSites"]],
        options: {
            "entityName": "sites.Location",
            "name": "default-sites-location-editor",
            "moduleName": "default-sites-location-editor",
            "moduleTitle": "Location (Object)",
            "moduleStateSlotName": "default-sites-location-editor"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-sites-location-editor',
            () => import('../lib/ui/modules/DataEditor.js'),
            () => import('./modules/sites/models/LocationModel.js'),
        ),
    },
    {
        name: 'default-model-sections-storedmodelsection-browser',
        path: '/default/model-sections/storedmodelsection',
        privileges: [["adminGeotechData"],["manageGeotechData"]],
        options: {
            "entityName": "model-sections.StoredModelSection",
            "name": "default-model-sections-storedmodelsection-browser",
            "moduleName": "default-model-sections-storedmodelsection-browser",
            "moduleTitle": "Stored Model Sections",
            "moduleStateSlotName": "default-model-sections-storedmodelsection-browser"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-model-sections-storedmodelsection-browser',
            () => import('../lib/ui/modules/DataBrowser.js'),
            () => import('./modules/model-sections/models/StoredModelSectionModel.js'),
        ),
    },
    {
        name: 'default-model-sections-storedmodelsection-editor',
        path: '/default/model-sections/storedmodelsection/:id',
        privileges: [["adminGeotechData"],["manageGeotechData"]],
        options: {
            "entityName": "model-sections.StoredModelSection",
            "name": "default-model-sections-storedmodelsection-editor",
            "moduleName": "default-model-sections-storedmodelsection-editor",
            "moduleTitle": "Stored Model Section",
            "moduleStateSlotName": "default-model-sections-storedmodelsection-editor"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-model-sections-storedmodelsection-editor',
            () => import('../lib/ui/modules/DataEditor.js'),
            () => import('./modules/model-sections/models/StoredModelSectionModel.js'),
        ),
    },
    {
        name: 'default-model-sections-modelsection-browser',
        path: '/default/model-sections/modelsection',
        privileges: [["adminGeotechData"],["manageGeotechData"]],
        options: {
            "entityName": "model-sections.ModelSection",
            "name": "default-model-sections-modelsection-browser",
            "moduleName": "default-model-sections-modelsection-browser",
            "moduleTitle": "Model Sections",
            "moduleStateSlotName": "default-model-sections-modelsection-browser"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-model-sections-modelsection-browser',
            () => import('../lib/ui/modules/DataBrowser.js'),
            () => import('./modules/model-sections/models/ModelSectionModel.js'),
        ),
    },
    {
        name: 'default-model-sections-modelsection-editor',
        path: '/default/model-sections/modelsection/:id',
        privileges: [["adminGeotechData"],["manageGeotechData"]],
        options: {
            "entityName": "model-sections.ModelSection",
            "name": "default-model-sections-modelsection-editor",
            "moduleName": "default-model-sections-modelsection-editor",
            "moduleTitle": "Model Section",
            "moduleStateSlotName": "default-model-sections-modelsection-editor"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-model-sections-modelsection-editor',
            () => import('../lib/ui/modules/DataEditor.js'),
            () => import('./modules/model-sections/models/ModelSectionModel.js'),
        ),
    },
    {
        name: 'default-model-sections-modelinterval-browser',
        path: '/default/model-sections/modelinterval',
        privileges: null,
        options: {
            "entityName": "model-sections.ModelInterval",
            "name": "default-model-sections-modelinterval-browser",
            "moduleName": "default-model-sections-modelinterval-browser",
            "moduleTitle": "Model Intervals",
            "moduleStateSlotName": "default-model-sections-modelinterval-browser"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-model-sections-modelinterval-browser',
            () => import('../lib/ui/modules/DataBrowser.js'),
            () => import('./modules/model-sections/models/ModelIntervalModel.js'),
        ),
    },
    {
        name: 'default-model-sections-modelinterval-editor',
        path: '/default/model-sections/modelinterval/:id',
        privileges: null,
        options: {
            "entityName": "model-sections.ModelInterval",
            "name": "default-model-sections-modelinterval-editor",
            "moduleName": "default-model-sections-modelinterval-editor",
            "moduleTitle": "Model Interval",
            "moduleStateSlotName": "default-model-sections-modelinterval-editor"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-model-sections-modelinterval-editor',
            () => import('../lib/ui/modules/DataEditor.js'),
            () => import('./modules/model-sections/models/ModelIntervalModel.js'),
        ),
    },
    {
        name: 'default-model-sections-geoboundarytype-browser',
        path: '/default/model-sections/geoboundarytype',
        privileges: null,
        options: {
            "entityName": "model-sections.GeoBoundaryType",
            "name": "default-model-sections-geoboundarytype-browser",
            "moduleName": "default-model-sections-geoboundarytype-browser",
            "moduleTitle": "Boundary Types",
            "moduleStateSlotName": "default-model-sections-geoboundarytype-browser"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-model-sections-geoboundarytype-browser',
            () => import('../lib/ui/modules/DataBrowser.js'),
            () => import('./modules/model-sections/models/GeoBoundaryTypeModel.js'),
        ),
    },
    {
        name: 'default-model-sections-geoboundarytype-editor',
        path: '/default/model-sections/geoboundarytype/:id',
        privileges: null,
        options: {
            "entityName": "model-sections.GeoBoundaryType",
            "name": "default-model-sections-geoboundarytype-editor",
            "moduleName": "default-model-sections-geoboundarytype-editor",
            "moduleTitle": "Boundary Type",
            "moduleStateSlotName": "default-model-sections-geoboundarytype-editor"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-model-sections-geoboundarytype-editor',
            () => import('../lib/ui/modules/DataEditor.js'),
            () => import('./modules/model-sections/models/GeoBoundaryTypeModel.js'),
        ),
    },
    {
        name: 'default-model-sections-geoboundary-browser',
        path: '/default/model-sections/geoboundary',
        privileges: [["adminGeotechData"],["manageGeotechData"]],
        options: {
            "entityName": "model-sections.GeoBoundary",
            "name": "default-model-sections-geoboundary-browser",
            "moduleName": "default-model-sections-geoboundary-browser",
            "moduleTitle": "Section Boundaries",
            "moduleStateSlotName": "default-model-sections-geoboundary-browser"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-model-sections-geoboundary-browser',
            () => import('../lib/ui/modules/DataBrowser.js'),
            () => import('./modules/model-sections/models/GeoBoundaryModel.js'),
        ),
    },
    {
        name: 'default-model-sections-geoboundary-editor',
        path: '/default/model-sections/geoboundary/:id',
        privileges: [["adminGeotechData"],["manageGeotechData"]],
        options: {
            "entityName": "model-sections.GeoBoundary",
            "name": "default-model-sections-geoboundary-editor",
            "moduleName": "default-model-sections-geoboundary-editor",
            "moduleTitle": "Section Boundary",
            "moduleStateSlotName": "default-model-sections-geoboundary-editor"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-model-sections-geoboundary-editor',
            () => import('../lib/ui/modules/DataEditor.js'),
            () => import('./modules/model-sections/models/GeoBoundaryModel.js'),
        ),
    },
    {
        name: 'default-model-sections-geoloadtype-browser',
        path: '/default/model-sections/geoloadtype',
        privileges: null,
        options: {
            "entityName": "model-sections.GeoLoadType",
            "name": "default-model-sections-geoloadtype-browser",
            "moduleName": "default-model-sections-geoloadtype-browser",
            "moduleTitle": "Load Types",
            "moduleStateSlotName": "default-model-sections-geoloadtype-browser"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-model-sections-geoloadtype-browser',
            () => import('../lib/ui/modules/DataBrowser.js'),
            () => import('./modules/model-sections/models/GeoLoadTypeModel.js'),
        ),
    },
    {
        name: 'default-model-sections-geoloadtype-editor',
        path: '/default/model-sections/geoloadtype/:id',
        privileges: null,
        options: {
            "entityName": "model-sections.GeoLoadType",
            "name": "default-model-sections-geoloadtype-editor",
            "moduleName": "default-model-sections-geoloadtype-editor",
            "moduleTitle": "Load Type",
            "moduleStateSlotName": "default-model-sections-geoloadtype-editor"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-model-sections-geoloadtype-editor',
            () => import('../lib/ui/modules/DataEditor.js'),
            () => import('./modules/model-sections/models/GeoLoadTypeModel.js'),
        ),
    },
    {
        name: 'default-model-sections-geoloaddirectiontype-browser',
        path: '/default/model-sections/geoloaddirectiontype',
        privileges: null,
        options: {
            "entityName": "model-sections.GeoLoadDirectionType",
            "name": "default-model-sections-geoloaddirectiontype-browser",
            "moduleName": "default-model-sections-geoloaddirectiontype-browser",
            "moduleTitle": "Load Direction Types",
            "moduleStateSlotName": "default-model-sections-geoloaddirectiontype-browser"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-model-sections-geoloaddirectiontype-browser',
            () => import('../lib/ui/modules/DataBrowser.js'),
            () => import('./modules/model-sections/models/GeoLoadDirectionTypeModel.js'),
        ),
    },
    {
        name: 'default-model-sections-geoloaddirectiontype-editor',
        path: '/default/model-sections/geoloaddirectiontype/:id',
        privileges: null,
        options: {
            "entityName": "model-sections.GeoLoadDirectionType",
            "name": "default-model-sections-geoloaddirectiontype-editor",
            "moduleName": "default-model-sections-geoloaddirectiontype-editor",
            "moduleTitle": "Load Direction Type",
            "moduleStateSlotName": "default-model-sections-geoloaddirectiontype-editor"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-model-sections-geoloaddirectiontype-editor',
            () => import('../lib/ui/modules/DataEditor.js'),
            () => import('./modules/model-sections/models/GeoLoadDirectionTypeModel.js'),
        ),
    },
    {
        name: 'default-model-sections-geoload-browser',
        path: '/default/model-sections/geoload',
        privileges: [["adminGeotechData"],["manageGeotechData"]],
        options: {
            "entityName": "model-sections.GeoLoad",
            "name": "default-model-sections-geoload-browser",
            "moduleName": "default-model-sections-geoload-browser",
            "moduleTitle": "Loads",
            "moduleStateSlotName": "default-model-sections-geoload-browser"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-model-sections-geoload-browser',
            () => import('../lib/ui/modules/DataBrowser.js'),
            () => import('./modules/model-sections/models/GeoLoadModel.js'),
        ),
    },
    {
        name: 'default-model-sections-geoload-editor',
        path: '/default/model-sections/geoload/:id',
        privileges: [["adminGeotechData"],["manageGeotechData"]],
        options: {
            "entityName": "model-sections.GeoLoad",
            "name": "default-model-sections-geoload-editor",
            "moduleName": "default-model-sections-geoload-editor",
            "moduleTitle": "Load",
            "moduleStateSlotName": "default-model-sections-geoload-editor"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-model-sections-geoload-editor',
            () => import('../lib/ui/modules/DataEditor.js'),
            () => import('./modules/model-sections/models/GeoLoadModel.js'),
        ),
    },
    {
        name: 'default-model-sections-geoobjectkind-browser',
        path: '/default/model-sections/geoobjectkind',
        privileges: null,
        options: {
            "entityName": "model-sections.GeoObjectKind",
            "name": "default-model-sections-geoobjectkind-browser",
            "moduleName": "default-model-sections-geoobjectkind-browser",
            "moduleTitle": "Object Kinds",
            "moduleStateSlotName": "default-model-sections-geoobjectkind-browser"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-model-sections-geoobjectkind-browser',
            () => import('../lib/ui/modules/DataBrowser.js'),
            () => import('./modules/model-sections/models/GeoObjectKindModel.js'),
        ),
    },
    {
        name: 'default-model-sections-geoobjectkind-editor',
        path: '/default/model-sections/geoobjectkind/:id',
        privileges: null,
        options: {
            "entityName": "model-sections.GeoObjectKind",
            "name": "default-model-sections-geoobjectkind-editor",
            "moduleName": "default-model-sections-geoobjectkind-editor",
            "moduleTitle": "Object Kind",
            "moduleStateSlotName": "default-model-sections-geoobjectkind-editor"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-model-sections-geoobjectkind-editor',
            () => import('../lib/ui/modules/DataEditor.js'),
            () => import('./modules/model-sections/models/GeoObjectKindModel.js'),
        ),
    },
    {
        name: 'default-model-sections-geoobjecttype-browser',
        path: '/default/model-sections/geoobjecttype',
        privileges: null,
        options: {
            "entityName": "model-sections.GeoObjectType",
            "name": "default-model-sections-geoobjecttype-browser",
            "moduleName": "default-model-sections-geoobjecttype-browser",
            "moduleTitle": "Object Types",
            "moduleStateSlotName": "default-model-sections-geoobjecttype-browser"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-model-sections-geoobjecttype-browser',
            () => import('../lib/ui/modules/DataBrowser.js'),
            () => import('./modules/model-sections/models/GeoObjectTypeModel.js'),
        ),
    },
    {
        name: 'default-model-sections-geoobjecttype-editor',
        path: '/default/model-sections/geoobjecttype/:id',
        privileges: null,
        options: {
            "entityName": "model-sections.GeoObjectType",
            "name": "default-model-sections-geoobjecttype-editor",
            "moduleName": "default-model-sections-geoobjecttype-editor",
            "moduleTitle": "Object Type",
            "moduleStateSlotName": "default-model-sections-geoobjecttype-editor"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-model-sections-geoobjecttype-editor',
            () => import('../lib/ui/modules/DataEditor.js'),
            () => import('./modules/model-sections/models/GeoObjectTypeModel.js'),
        ),
    },
    {
        name: 'default-model-sections-geoobject-browser',
        path: '/default/model-sections/geoobject',
        privileges: [["adminGeotechData"],["manageGeotechData"]],
        options: {
            "entityName": "model-sections.GeoObject",
            "name": "default-model-sections-geoobject-browser",
            "moduleName": "default-model-sections-geoobject-browser",
            "moduleTitle": "Objects",
            "moduleStateSlotName": "default-model-sections-geoobject-browser"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-model-sections-geoobject-browser',
            () => import('../lib/ui/modules/DataBrowser.js'),
            () => import('./modules/model-sections/models/GeoObjectModel.js'),
        ),
    },
    {
        name: 'default-model-sections-geoobject-editor',
        path: '/default/model-sections/geoobject/:id',
        privileges: [["adminGeotechData"],["manageGeotechData"]],
        options: {
            "entityName": "model-sections.GeoObject",
            "name": "default-model-sections-geoobject-editor",
            "moduleName": "default-model-sections-geoobject-editor",
            "moduleTitle": "Object",
            "moduleStateSlotName": "default-model-sections-geoobject-editor"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-model-sections-geoobject-editor',
            () => import('../lib/ui/modules/DataEditor.js'),
            () => import('./modules/model-sections/models/GeoObjectModel.js'),
        ),
    },
    {
        name: 'default-model-sections-geoobjecttbm-browser',
        path: '/default/model-sections/geoobjecttbm',
        privileges: [["adminGeotechData"],["manageGeotechData"]],
        options: {
            "entityName": "model-sections.GeoObjectTbm",
            "name": "default-model-sections-geoobjecttbm-browser",
            "moduleName": "default-model-sections-geoobjecttbm-browser",
            "moduleTitle": "Object Tbms",
            "moduleStateSlotName": "default-model-sections-geoobjecttbm-browser"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-model-sections-geoobjecttbm-browser',
            () => import('../lib/ui/modules/DataBrowser.js'),
            () => import('./modules/model-sections/models/GeoObjectTbmModel.js'),
        ),
    },
    {
        name: 'default-model-sections-geoobjecttbm-editor',
        path: '/default/model-sections/geoobjecttbm/:id',
        privileges: [["adminGeotechData"],["manageGeotechData"]],
        options: {
            "entityName": "model-sections.GeoObjectTbm",
            "name": "default-model-sections-geoobjecttbm-editor",
            "moduleName": "default-model-sections-geoobjecttbm-editor",
            "moduleTitle": "Object Tbm",
            "moduleStateSlotName": "default-model-sections-geoobjecttbm-editor"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-model-sections-geoobjecttbm-editor',
            () => import('../lib/ui/modules/DataEditor.js'),
            () => import('./modules/model-sections/models/GeoObjectTbmModel.js'),
        ),
    },
    {
        name: 'default-model-sections-geoobjecttbmboundary-browser',
        path: '/default/model-sections/geoobjecttbmboundary',
        privileges: [["adminGeotechData"],["manageGeotechData"]],
        options: {
            "entityName": "model-sections.GeoObjectTbmBoundary",
            "name": "default-model-sections-geoobjecttbmboundary-browser",
            "moduleName": "default-model-sections-geoobjecttbmboundary-browser",
            "moduleTitle": "Tbm Levels",
            "moduleStateSlotName": "default-model-sections-geoobjecttbmboundary-browser"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-model-sections-geoobjecttbmboundary-browser',
            () => import('../lib/ui/modules/DataBrowser.js'),
            () => import('./modules/model-sections/models/GeoObjectTbmBoundaryModel.js'),
        ),
    },
    {
        name: 'default-model-sections-geoobjecttbmboundary-editor',
        path: '/default/model-sections/geoobjecttbmboundary/:id',
        privileges: [["adminGeotechData"],["manageGeotechData"]],
        options: {
            "entityName": "model-sections.GeoObjectTbmBoundary",
            "name": "default-model-sections-geoobjecttbmboundary-editor",
            "moduleName": "default-model-sections-geoobjecttbmboundary-editor",
            "moduleTitle": "Tbm Level",
            "moduleStateSlotName": "default-model-sections-geoobjecttbmboundary-editor"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-model-sections-geoobjecttbmboundary-editor',
            () => import('../lib/ui/modules/DataEditor.js'),
            () => import('./modules/model-sections/models/GeoObjectTbmBoundaryModel.js'),
        ),
    },
    {
        name: 'default-model-sections-geoobjecttbmsensor-browser',
        path: '/default/model-sections/geoobjecttbmsensor',
        privileges: [["adminGeotechData"],["manageGeotechData"]],
        options: {
            "entityName": "model-sections.GeoObjectTbmSensor",
            "name": "default-model-sections-geoobjecttbmsensor-browser",
            "moduleName": "default-model-sections-geoobjecttbmsensor-browser",
            "moduleTitle": "Tbm Face Pressure Sensors",
            "moduleStateSlotName": "default-model-sections-geoobjecttbmsensor-browser"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-model-sections-geoobjecttbmsensor-browser',
            () => import('../lib/ui/modules/DataBrowser.js'),
            () => import('./modules/model-sections/models/GeoObjectTbmSensorModel.js'),
        ),
    },
    {
        name: 'default-model-sections-geoobjecttbmsensor-editor',
        path: '/default/model-sections/geoobjecttbmsensor/:id',
        privileges: [["adminGeotechData"],["manageGeotechData"]],
        options: {
            "entityName": "model-sections.GeoObjectTbmSensor",
            "name": "default-model-sections-geoobjecttbmsensor-editor",
            "moduleName": "default-model-sections-geoobjecttbmsensor-editor",
            "moduleTitle": "Tbm Face Pressure Sensor",
            "moduleStateSlotName": "default-model-sections-geoobjecttbmsensor-editor"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-model-sections-geoobjecttbmsensor-editor',
            () => import('../lib/ui/modules/DataEditor.js'),
            () => import('./modules/model-sections/models/GeoObjectTbmSensorModel.js'),
        ),
    },
    {
        name: 'default-geo-sections-watersurfacestresscalculationmethod-browser',
        path: '/default/geo-sections/watersurfacestresscalculationmethod',
        privileges: null,
        options: {
            "entityName": "geo-sections.WaterSurfaceStressCalculationMethod",
            "name": "default-geo-sections-watersurfacestresscalculationmethod-browser",
            "moduleName": "default-geo-sections-watersurfacestresscalculationmethod-browser",
            "moduleTitle": "Water Surface Stress Calc Methods",
            "moduleStateSlotName": "default-geo-sections-watersurfacestresscalculationmethod-browser"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-geo-sections-watersurfacestresscalculationmethod-browser',
            () => import('../lib/ui/modules/DataBrowser.js'),
            () => import('./modules/geo-sections/models/WaterSurfaceStressCalculationMethodModel.js'),
        ),
    },
    {
        name: 'default-geo-sections-watersurfacestresscalculationmethod-editor',
        path: '/default/geo-sections/watersurfacestresscalculationmethod/:id',
        privileges: null,
        options: {
            "entityName": "geo-sections.WaterSurfaceStressCalculationMethod",
            "name": "default-geo-sections-watersurfacestresscalculationmethod-editor",
            "moduleName": "default-geo-sections-watersurfacestresscalculationmethod-editor",
            "moduleTitle": "Water Surface Stress Calc Method",
            "moduleStateSlotName": "default-geo-sections-watersurfacestresscalculationmethod-editor"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-geo-sections-watersurfacestresscalculationmethod-editor',
            () => import('../lib/ui/modules/DataEditor.js'),
            () => import('./modules/geo-sections/models/WaterSurfaceStressCalculationMethodModel.js'),
        ),
    },
    {
        name: 'default-geo-sections-piezometricheadcalculationmethod-browser',
        path: '/default/geo-sections/piezometricheadcalculationmethod',
        privileges: null,
        options: {
            "entityName": "geo-sections.PiezometricHeadCalculationMethod",
            "name": "default-geo-sections-piezometricheadcalculationmethod-browser",
            "moduleName": "default-geo-sections-piezometricheadcalculationmethod-browser",
            "moduleTitle": "Piezometric Head Calc Methods",
            "moduleStateSlotName": "default-geo-sections-piezometricheadcalculationmethod-browser"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-geo-sections-piezometricheadcalculationmethod-browser',
            () => import('../lib/ui/modules/DataBrowser.js'),
            () => import('./modules/geo-sections/models/PiezometricHeadCalculationMethodModel.js'),
        ),
    },
    {
        name: 'default-geo-sections-piezometricheadcalculationmethod-editor',
        path: '/default/geo-sections/piezometricheadcalculationmethod/:id',
        privileges: null,
        options: {
            "entityName": "geo-sections.PiezometricHeadCalculationMethod",
            "name": "default-geo-sections-piezometricheadcalculationmethod-editor",
            "moduleName": "default-geo-sections-piezometricheadcalculationmethod-editor",
            "moduleTitle": "Piezometric Head Calc Method",
            "moduleStateSlotName": "default-geo-sections-piezometricheadcalculationmethod-editor"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-geo-sections-piezometricheadcalculationmethod-editor',
            () => import('../lib/ui/modules/DataEditor.js'),
            () => import('./modules/geo-sections/models/PiezometricHeadCalculationMethodModel.js'),
        ),
    },
    {
        name: 'default-geo-sections-k0calculationmethod-browser',
        path: '/default/geo-sections/k0calculationmethod',
        privileges: null,
        options: {
            "entityName": "geo-sections.K0CalculationMethod",
            "name": "default-geo-sections-k0calculationmethod-browser",
            "moduleName": "default-geo-sections-k0calculationmethod-browser",
            "moduleTitle": "K 0 Methods",
            "moduleStateSlotName": "default-geo-sections-k0calculationmethod-browser"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-geo-sections-k0calculationmethod-browser',
            () => import('../lib/ui/modules/DataBrowser.js'),
            () => import('./modules/geo-sections/models/K0CalculationMethodModel.js'),
        ),
    },
    {
        name: 'default-geo-sections-k0calculationmethod-editor',
        path: '/default/geo-sections/k0calculationmethod/:id',
        privileges: null,
        options: {
            "entityName": "geo-sections.K0CalculationMethod",
            "name": "default-geo-sections-k0calculationmethod-editor",
            "moduleName": "default-geo-sections-k0calculationmethod-editor",
            "moduleTitle": "K 0 Method",
            "moduleStateSlotName": "default-geo-sections-k0calculationmethod-editor"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-geo-sections-k0calculationmethod-editor',
            () => import('../lib/ui/modules/DataEditor.js'),
            () => import('./modules/geo-sections/models/K0CalculationMethodModel.js'),
        ),
    },
    {
        name: 'default-geo-sections-bulkunitweightgamacalculationmethod-browser',
        path: '/default/geo-sections/bulkunitweightgamacalculationmethod',
        privileges: null,
        options: {
            "entityName": "geo-sections.BulkUnitWeightGamaCalculationMethod",
            "name": "default-geo-sections-bulkunitweightgamacalculationmethod-browser",
            "moduleName": "default-geo-sections-bulkunitweightgamacalculationmethod-browser",
            "moduleTitle": "Bulk Unit Weight Gama Calc Methods",
            "moduleStateSlotName": "default-geo-sections-bulkunitweightgamacalculationmethod-browser"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-geo-sections-bulkunitweightgamacalculationmethod-browser',
            () => import('../lib/ui/modules/DataBrowser.js'),
            () => import('./modules/geo-sections/models/BulkUnitWeightGamaCalculationMethodModel.js'),
        ),
    },
    {
        name: 'default-geo-sections-bulkunitweightgamacalculationmethod-editor',
        path: '/default/geo-sections/bulkunitweightgamacalculationmethod/:id',
        privileges: null,
        options: {
            "entityName": "geo-sections.BulkUnitWeightGamaCalculationMethod",
            "name": "default-geo-sections-bulkunitweightgamacalculationmethod-editor",
            "moduleName": "default-geo-sections-bulkunitweightgamacalculationmethod-editor",
            "moduleTitle": "Bulk Unit Weight Gama Calc Method",
            "moduleStateSlotName": "default-geo-sections-bulkunitweightgamacalculationmethod-editor"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-geo-sections-bulkunitweightgamacalculationmethod-editor',
            () => import('../lib/ui/modules/DataEditor.js'),
            () => import('./modules/geo-sections/models/BulkUnitWeightGamaCalculationMethodModel.js'),
        ),
    },
    {
        name: 'default-geo-sections-minbulkunitweightgamamincalculationmethod-browser',
        path: '/default/geo-sections/minbulkunitweightgamamincalculationmethod',
        privileges: null,
        options: {
            "entityName": "geo-sections.MinBulkUnitWeightGamaMinCalculationMethod",
            "name": "default-geo-sections-minbulkunitweightgamamincalculationmethod-browser",
            "moduleName": "default-geo-sections-minbulkunitweightgamamincalculationmethod-browser",
            "moduleTitle": "Min Bulk Unit Weight Gama Min Calc Methods",
            "moduleStateSlotName": "default-geo-sections-minbulkunitweightgamamincalculationmethod-browser"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-geo-sections-minbulkunitweightgamamincalculationmethod-browser',
            () => import('../lib/ui/modules/DataBrowser.js'),
            () => import('./modules/geo-sections/models/MinBulkUnitWeightGamaMinCalculationMethodModel.js'),
        ),
    },
    {
        name: 'default-geo-sections-minbulkunitweightgamamincalculationmethod-editor',
        path: '/default/geo-sections/minbulkunitweightgamamincalculationmethod/:id',
        privileges: null,
        options: {
            "entityName": "geo-sections.MinBulkUnitWeightGamaMinCalculationMethod",
            "name": "default-geo-sections-minbulkunitweightgamamincalculationmethod-editor",
            "moduleName": "default-geo-sections-minbulkunitweightgamamincalculationmethod-editor",
            "moduleTitle": "Min Bulk Unit Weight Gama Min Calc Method",
            "moduleStateSlotName": "default-geo-sections-minbulkunitweightgamamincalculationmethod-editor"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-geo-sections-minbulkunitweightgamamincalculationmethod-editor',
            () => import('../lib/ui/modules/DataEditor.js'),
            () => import('./modules/geo-sections/models/MinBulkUnitWeightGamaMinCalculationMethodModel.js'),
        ),
    },
    {
        name: 'default-geo-sections-saturatedunitweightgamasatcalculationmethod-browser',
        path: '/default/geo-sections/saturatedunitweightgamasatcalculationmethod',
        privileges: null,
        options: {
            "entityName": "geo-sections.SaturatedUnitWeightGamaSatCalculationMethod",
            "name": "default-geo-sections-saturatedunitweightgamasatcalculationmethod-browser",
            "moduleName": "default-geo-sections-saturatedunitweightgamasatcalculationmethod-browser",
            "moduleTitle": "Saturated Unit Weight Gama Sat Calc Methods",
            "moduleStateSlotName": "default-geo-sections-saturatedunitweightgamasatcalculationmethod-browser"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-geo-sections-saturatedunitweightgamasatcalculationmethod-browser',
            () => import('../lib/ui/modules/DataBrowser.js'),
            () => import('./modules/geo-sections/models/SaturatedUnitWeightGamaSatCalculationMethodModel.js'),
        ),
    },
    {
        name: 'default-geo-sections-saturatedunitweightgamasatcalculationmethod-editor',
        path: '/default/geo-sections/saturatedunitweightgamasatcalculationmethod/:id',
        privileges: null,
        options: {
            "entityName": "geo-sections.SaturatedUnitWeightGamaSatCalculationMethod",
            "name": "default-geo-sections-saturatedunitweightgamasatcalculationmethod-editor",
            "moduleName": "default-geo-sections-saturatedunitweightgamasatcalculationmethod-editor",
            "moduleTitle": "Saturated Unit Weight Gama Sat Calc Method",
            "moduleStateSlotName": "default-geo-sections-saturatedunitweightgamasatcalculationmethod-editor"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-geo-sections-saturatedunitweightgamasatcalculationmethod-editor',
            () => import('../lib/ui/modules/DataEditor.js'),
            () => import('./modules/geo-sections/models/SaturatedUnitWeightGamaSatCalculationMethodModel.js'),
        ),
    },
    {
        name: 'default-geo-sections-submergedunitweightgamasubcalculationmethod-browser',
        path: '/default/geo-sections/submergedunitweightgamasubcalculationmethod',
        privileges: null,
        options: {
            "entityName": "geo-sections.SubmergedUnitWeightGamaSubCalculationMethod",
            "name": "default-geo-sections-submergedunitweightgamasubcalculationmethod-browser",
            "moduleName": "default-geo-sections-submergedunitweightgamasubcalculationmethod-browser",
            "moduleTitle": "Submerged Unit Weight Gama Sub Calc Methods",
            "moduleStateSlotName": "default-geo-sections-submergedunitweightgamasubcalculationmethod-browser"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-geo-sections-submergedunitweightgamasubcalculationmethod-browser',
            () => import('../lib/ui/modules/DataBrowser.js'),
            () => import('./modules/geo-sections/models/SubmergedUnitWeightGamaSubCalculationMethodModel.js'),
        ),
    },
    {
        name: 'default-geo-sections-submergedunitweightgamasubcalculationmethod-editor',
        path: '/default/geo-sections/submergedunitweightgamasubcalculationmethod/:id',
        privileges: null,
        options: {
            "entityName": "geo-sections.SubmergedUnitWeightGamaSubCalculationMethod",
            "name": "default-geo-sections-submergedunitweightgamasubcalculationmethod-editor",
            "moduleName": "default-geo-sections-submergedunitweightgamasubcalculationmethod-editor",
            "moduleTitle": "Submerged Unit Weight Gama Sub Calc Method",
            "moduleStateSlotName": "default-geo-sections-submergedunitweightgamasubcalculationmethod-editor"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-geo-sections-submergedunitweightgamasubcalculationmethod-editor',
            () => import('../lib/ui/modules/DataEditor.js'),
            () => import('./modules/geo-sections/models/SubmergedUnitWeightGamaSubCalculationMethodModel.js'),
        ),
    },
    {
        name: 'default-geo-sections-minsubmergedunitweightgamasubmincalculationmethod-browser',
        path: '/default/geo-sections/minsubmergedunitweightgamasubmincalculationmethod',
        privileges: null,
        options: {
            "entityName": "geo-sections.MinSubmergedUnitWeightGamaSubMinCalculationMethod",
            "name": "default-geo-sections-minsubmergedunitweightgamasubmincalculationmethod-browser",
            "moduleName": "default-geo-sections-minsubmergedunitweightgamasubmincalculationmethod-browser",
            "moduleTitle": "Min Submerged Unit Weight Gama Sub Min Calc Methods",
            "moduleStateSlotName": "default-geo-sections-minsubmergedunitweightgamasubmincalculationmethod-browser"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-geo-sections-minsubmergedunitweightgamasubmincalculationmethod-browser',
            () => import('../lib/ui/modules/DataBrowser.js'),
            () => import('./modules/geo-sections/models/MinSubmergedUnitWeightGamaSubMinCalculationMethodModel.js'),
        ),
    },
    {
        name: 'default-geo-sections-minsubmergedunitweightgamasubmincalculationmethod-editor',
        path: '/default/geo-sections/minsubmergedunitweightgamasubmincalculationmethod/:id',
        privileges: null,
        options: {
            "entityName": "geo-sections.MinSubmergedUnitWeightGamaSubMinCalculationMethod",
            "name": "default-geo-sections-minsubmergedunitweightgamasubmincalculationmethod-editor",
            "moduleName": "default-geo-sections-minsubmergedunitweightgamasubmincalculationmethod-editor",
            "moduleTitle": "Min Submerged Unit Weight Gama Sub Min Calc Method",
            "moduleStateSlotName": "default-geo-sections-minsubmergedunitweightgamasubmincalculationmethod-editor"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-geo-sections-minsubmergedunitweightgamasubmincalculationmethod-editor',
            () => import('../lib/ui/modules/DataEditor.js'),
            () => import('./modules/geo-sections/models/MinSubmergedUnitWeightGamaSubMinCalculationMethodModel.js'),
        ),
    },
    {
        name: 'default-geo-sections-dryunitweightgamadcalculationmethod-browser',
        path: '/default/geo-sections/dryunitweightgamadcalculationmethod',
        privileges: null,
        options: {
            "entityName": "geo-sections.DryUnitWeightGamaDCalculationMethod",
            "name": "default-geo-sections-dryunitweightgamadcalculationmethod-browser",
            "moduleName": "default-geo-sections-dryunitweightgamadcalculationmethod-browser",
            "moduleTitle": "Dry Unit Weight Gama D Calc Methods",
            "moduleStateSlotName": "default-geo-sections-dryunitweightgamadcalculationmethod-browser"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-geo-sections-dryunitweightgamadcalculationmethod-browser',
            () => import('../lib/ui/modules/DataBrowser.js'),
            () => import('./modules/geo-sections/models/DryUnitWeightGamaDCalculationMethodModel.js'),
        ),
    },
    {
        name: 'default-geo-sections-dryunitweightgamadcalculationmethod-editor',
        path: '/default/geo-sections/dryunitweightgamadcalculationmethod/:id',
        privileges: null,
        options: {
            "entityName": "geo-sections.DryUnitWeightGamaDCalculationMethod",
            "name": "default-geo-sections-dryunitweightgamadcalculationmethod-editor",
            "moduleName": "default-geo-sections-dryunitweightgamadcalculationmethod-editor",
            "moduleTitle": "Dry Unit Weight Gama D Calc Method",
            "moduleStateSlotName": "default-geo-sections-dryunitweightgamadcalculationmethod-editor"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-geo-sections-dryunitweightgamadcalculationmethod-editor',
            () => import('../lib/ui/modules/DataEditor.js'),
            () => import('./modules/geo-sections/models/DryUnitWeightGamaDCalculationMethodModel.js'),
        ),
    },
    {
        name: 'default-geo-sections-unitweightofsolidsgamascalculationmethod-browser',
        path: '/default/geo-sections/unitweightofsolidsgamascalculationmethod',
        privileges: null,
        options: {
            "entityName": "geo-sections.UnitWeightOfSolidsGamaSCalculationMethod",
            "name": "default-geo-sections-unitweightofsolidsgamascalculationmethod-browser",
            "moduleName": "default-geo-sections-unitweightofsolidsgamascalculationmethod-browser",
            "moduleTitle": "Unit Weight Of Solids Gama S Calc Methods",
            "moduleStateSlotName": "default-geo-sections-unitweightofsolidsgamascalculationmethod-browser"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-geo-sections-unitweightofsolidsgamascalculationmethod-browser',
            () => import('../lib/ui/modules/DataBrowser.js'),
            () => import('./modules/geo-sections/models/UnitWeightOfSolidsGamaSCalculationMethodModel.js'),
        ),
    },
    {
        name: 'default-geo-sections-unitweightofsolidsgamascalculationmethod-editor',
        path: '/default/geo-sections/unitweightofsolidsgamascalculationmethod/:id',
        privileges: null,
        options: {
            "entityName": "geo-sections.UnitWeightOfSolidsGamaSCalculationMethod",
            "name": "default-geo-sections-unitweightofsolidsgamascalculationmethod-editor",
            "moduleName": "default-geo-sections-unitweightofsolidsgamascalculationmethod-editor",
            "moduleTitle": "Unit Weight Of Solids Gama S Calc Method",
            "moduleStateSlotName": "default-geo-sections-unitweightofsolidsgamascalculationmethod-editor"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-geo-sections-unitweightofsolidsgamascalculationmethod-editor',
            () => import('../lib/ui/modules/DataEditor.js'),
            () => import('./modules/geo-sections/models/UnitWeightOfSolidsGamaSCalculationMethodModel.js'),
        ),
    },
    {
        name: 'default-geo-sections-minunitweightofsolidsgamasmincalculationmethod-browser',
        path: '/default/geo-sections/minunitweightofsolidsgamasmincalculationmethod',
        privileges: null,
        options: {
            "entityName": "geo-sections.MinUnitWeightOfSolidsGamaSMinCalculationMethod",
            "name": "default-geo-sections-minunitweightofsolidsgamasmincalculationmethod-browser",
            "moduleName": "default-geo-sections-minunitweightofsolidsgamasmincalculationmethod-browser",
            "moduleTitle": "Min Unit Weight Of Solids Gama S Min Calc Methods",
            "moduleStateSlotName": "default-geo-sections-minunitweightofsolidsgamasmincalculationmethod-browser"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-geo-sections-minunitweightofsolidsgamasmincalculationmethod-browser',
            () => import('../lib/ui/modules/DataBrowser.js'),
            () => import('./modules/geo-sections/models/MinUnitWeightOfSolidsGamaSMinCalculationMethodModel.js'),
        ),
    },
    {
        name: 'default-geo-sections-minunitweightofsolidsgamasmincalculationmethod-editor',
        path: '/default/geo-sections/minunitweightofsolidsgamasmincalculationmethod/:id',
        privileges: null,
        options: {
            "entityName": "geo-sections.MinUnitWeightOfSolidsGamaSMinCalculationMethod",
            "name": "default-geo-sections-minunitweightofsolidsgamasmincalculationmethod-editor",
            "moduleName": "default-geo-sections-minunitweightofsolidsgamasmincalculationmethod-editor",
            "moduleTitle": "Min Unit Weight Of Solids Gama S Min Calc Method",
            "moduleStateSlotName": "default-geo-sections-minunitweightofsolidsgamasmincalculationmethod-editor"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-geo-sections-minunitweightofsolidsgamasmincalculationmethod-editor',
            () => import('../lib/ui/modules/DataEditor.js'),
            () => import('./modules/geo-sections/models/MinUnitWeightOfSolidsGamaSMinCalculationMethodModel.js'),
        ),
    },
    {
        name: 'default-geo-sections-degreeofsaturationsrcalculationmethod-browser',
        path: '/default/geo-sections/degreeofsaturationsrcalculationmethod',
        privileges: null,
        options: {
            "entityName": "geo-sections.DegreeOfSaturationSrCalculationMethod",
            "name": "default-geo-sections-degreeofsaturationsrcalculationmethod-browser",
            "moduleName": "default-geo-sections-degreeofsaturationsrcalculationmethod-browser",
            "moduleTitle": "Degree Of Saturation Sr Calc Methods",
            "moduleStateSlotName": "default-geo-sections-degreeofsaturationsrcalculationmethod-browser"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-geo-sections-degreeofsaturationsrcalculationmethod-browser',
            () => import('../lib/ui/modules/DataBrowser.js'),
            () => import('./modules/geo-sections/models/DegreeOfSaturationSrCalculationMethodModel.js'),
        ),
    },
    {
        name: 'default-geo-sections-degreeofsaturationsrcalculationmethod-editor',
        path: '/default/geo-sections/degreeofsaturationsrcalculationmethod/:id',
        privileges: null,
        options: {
            "entityName": "geo-sections.DegreeOfSaturationSrCalculationMethod",
            "name": "default-geo-sections-degreeofsaturationsrcalculationmethod-editor",
            "moduleName": "default-geo-sections-degreeofsaturationsrcalculationmethod-editor",
            "moduleTitle": "Degree Of Saturation Sr Calc Method",
            "moduleStateSlotName": "default-geo-sections-degreeofsaturationsrcalculationmethod-editor"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-geo-sections-degreeofsaturationsrcalculationmethod-editor',
            () => import('../lib/ui/modules/DataEditor.js'),
            () => import('./modules/geo-sections/models/DegreeOfSaturationSrCalculationMethodModel.js'),
        ),
    },
    {
        name: 'default-geo-sections-moisturecontentwcalculationmethod-browser',
        path: '/default/geo-sections/moisturecontentwcalculationmethod',
        privileges: null,
        options: {
            "entityName": "geo-sections.MoistureContentWCalculationMethod",
            "name": "default-geo-sections-moisturecontentwcalculationmethod-browser",
            "moduleName": "default-geo-sections-moisturecontentwcalculationmethod-browser",
            "moduleTitle": "Moisture Content W Calc Methods",
            "moduleStateSlotName": "default-geo-sections-moisturecontentwcalculationmethod-browser"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-geo-sections-moisturecontentwcalculationmethod-browser',
            () => import('../lib/ui/modules/DataBrowser.js'),
            () => import('./modules/geo-sections/models/MoistureContentWCalculationMethodModel.js'),
        ),
    },
    {
        name: 'default-geo-sections-moisturecontentwcalculationmethod-editor',
        path: '/default/geo-sections/moisturecontentwcalculationmethod/:id',
        privileges: null,
        options: {
            "entityName": "geo-sections.MoistureContentWCalculationMethod",
            "name": "default-geo-sections-moisturecontentwcalculationmethod-editor",
            "moduleName": "default-geo-sections-moisturecontentwcalculationmethod-editor",
            "moduleTitle": "Moisture Content W Calc Method",
            "moduleStateSlotName": "default-geo-sections-moisturecontentwcalculationmethod-editor"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-geo-sections-moisturecontentwcalculationmethod-editor',
            () => import('../lib/ui/modules/DataEditor.js'),
            () => import('./modules/geo-sections/models/MoistureContentWCalculationMethodModel.js'),
        ),
    },
    {
        name: 'default-geo-sections-plasticityindexipcalculationmethod-browser',
        path: '/default/geo-sections/plasticityindexipcalculationmethod',
        privileges: null,
        options: {
            "entityName": "geo-sections.PlasticityIndexIpCalculationMethod",
            "name": "default-geo-sections-plasticityindexipcalculationmethod-browser",
            "moduleName": "default-geo-sections-plasticityindexipcalculationmethod-browser",
            "moduleTitle": "Plasticity Index Ip Calc Methods",
            "moduleStateSlotName": "default-geo-sections-plasticityindexipcalculationmethod-browser"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-geo-sections-plasticityindexipcalculationmethod-browser',
            () => import('../lib/ui/modules/DataBrowser.js'),
            () => import('./modules/geo-sections/models/PlasticityIndexIpCalculationMethodModel.js'),
        ),
    },
    {
        name: 'default-geo-sections-plasticityindexipcalculationmethod-editor',
        path: '/default/geo-sections/plasticityindexipcalculationmethod/:id',
        privileges: null,
        options: {
            "entityName": "geo-sections.PlasticityIndexIpCalculationMethod",
            "name": "default-geo-sections-plasticityindexipcalculationmethod-editor",
            "moduleName": "default-geo-sections-plasticityindexipcalculationmethod-editor",
            "moduleTitle": "Plasticity Index Ip Calc Method",
            "moduleStateSlotName": "default-geo-sections-plasticityindexipcalculationmethod-editor"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-geo-sections-plasticityindexipcalculationmethod-editor',
            () => import('../lib/ui/modules/DataEditor.js'),
            () => import('./modules/geo-sections/models/PlasticityIndexIpCalculationMethodModel.js'),
        ),
    },
    {
        name: 'default-geo-sections-porosityncalculationmethod-browser',
        path: '/default/geo-sections/porosityncalculationmethod',
        privileges: null,
        options: {
            "entityName": "geo-sections.PorosityNCalculationMethod",
            "name": "default-geo-sections-porosityncalculationmethod-browser",
            "moduleName": "default-geo-sections-porosityncalculationmethod-browser",
            "moduleTitle": "Porosity N Calc Methods",
            "moduleStateSlotName": "default-geo-sections-porosityncalculationmethod-browser"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-geo-sections-porosityncalculationmethod-browser',
            () => import('../lib/ui/modules/DataBrowser.js'),
            () => import('./modules/geo-sections/models/PorosityNCalculationMethodModel.js'),
        ),
    },
    {
        name: 'default-geo-sections-porosityncalculationmethod-editor',
        path: '/default/geo-sections/porosityncalculationmethod/:id',
        privileges: null,
        options: {
            "entityName": "geo-sections.PorosityNCalculationMethod",
            "name": "default-geo-sections-porosityncalculationmethod-editor",
            "moduleName": "default-geo-sections-porosityncalculationmethod-editor",
            "moduleTitle": "Porosity N Calc Method",
            "moduleStateSlotName": "default-geo-sections-porosityncalculationmethod-editor"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-geo-sections-porosityncalculationmethod-editor',
            () => import('../lib/ui/modules/DataEditor.js'),
            () => import('./modules/geo-sections/models/PorosityNCalculationMethodModel.js'),
        ),
    },
    {
        name: 'default-geo-sections-voidratioecalculationmethod-browser',
        path: '/default/geo-sections/voidratioecalculationmethod',
        privileges: null,
        options: {
            "entityName": "geo-sections.VoidRatioECalculationMethod",
            "name": "default-geo-sections-voidratioecalculationmethod-browser",
            "moduleName": "default-geo-sections-voidratioecalculationmethod-browser",
            "moduleTitle": "Void Ratio E Calc Methods",
            "moduleStateSlotName": "default-geo-sections-voidratioecalculationmethod-browser"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-geo-sections-voidratioecalculationmethod-browser',
            () => import('../lib/ui/modules/DataBrowser.js'),
            () => import('./modules/geo-sections/models/VoidRatioECalculationMethodModel.js'),
        ),
    },
    {
        name: 'default-geo-sections-voidratioecalculationmethod-editor',
        path: '/default/geo-sections/voidratioecalculationmethod/:id',
        privileges: null,
        options: {
            "entityName": "geo-sections.VoidRatioECalculationMethod",
            "name": "default-geo-sections-voidratioecalculationmethod-editor",
            "moduleName": "default-geo-sections-voidratioecalculationmethod-editor",
            "moduleTitle": "Void Ratio E Calc Method",
            "moduleStateSlotName": "default-geo-sections-voidratioecalculationmethod-editor"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-geo-sections-voidratioecalculationmethod-editor',
            () => import('../lib/ui/modules/DataEditor.js'),
            () => import('./modules/geo-sections/models/VoidRatioECalculationMethodModel.js'),
        ),
    },
    {
        name: 'default-geo-sections-soilunitweightgamacalculationmethod-browser',
        path: '/default/geo-sections/soilunitweightgamacalculationmethod',
        privileges: null,
        options: {
            "entityName": "geo-sections.SoilUnitWeightGamaCalculationMethod",
            "name": "default-geo-sections-soilunitweightgamacalculationmethod-browser",
            "moduleName": "default-geo-sections-soilunitweightgamacalculationmethod-browser",
            "moduleTitle": "Soil Unit Weight Gama Calc Methods",
            "moduleStateSlotName": "default-geo-sections-soilunitweightgamacalculationmethod-browser"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-geo-sections-soilunitweightgamacalculationmethod-browser',
            () => import('../lib/ui/modules/DataBrowser.js'),
            () => import('./modules/geo-sections/models/SoilUnitWeightGamaCalculationMethodModel.js'),
        ),
    },
    {
        name: 'default-geo-sections-soilunitweightgamacalculationmethod-editor',
        path: '/default/geo-sections/soilunitweightgamacalculationmethod/:id',
        privileges: null,
        options: {
            "entityName": "geo-sections.SoilUnitWeightGamaCalculationMethod",
            "name": "default-geo-sections-soilunitweightgamacalculationmethod-editor",
            "moduleName": "default-geo-sections-soilunitweightgamacalculationmethod-editor",
            "moduleTitle": "Soil Unit Weight Gama Calc Method",
            "moduleStateSlotName": "default-geo-sections-soilunitweightgamacalculationmethod-editor"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-geo-sections-soilunitweightgamacalculationmethod-editor',
            () => import('../lib/ui/modules/DataEditor.js'),
            () => import('./modules/geo-sections/models/SoilUnitWeightGamaCalculationMethodModel.js'),
        ),
    },
    {
        name: 'default-geo-sections-geomasstype-browser',
        path: '/default/geo-sections/geomasstype',
        privileges: [["adminSystem"]],
        options: {
            "entityName": "geo-sections.GeoMassType",
            "name": "default-geo-sections-geomasstype-browser",
            "moduleName": "default-geo-sections-geomasstype-browser",
            "moduleTitle": "Mass Types",
            "moduleStateSlotName": "default-geo-sections-geomasstype-browser"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-geo-sections-geomasstype-browser',
            () => import('../lib/ui/modules/DataBrowser.js'),
            () => import('./modules/geo-sections/models/GeoMassTypeModel.js'),
        ),
    },
    {
        name: 'default-geo-sections-geomasstype-editor',
        path: '/default/geo-sections/geomasstype/:id',
        privileges: [["adminSystem"]],
        options: {
            "entityName": "geo-sections.GeoMassType",
            "name": "default-geo-sections-geomasstype-editor",
            "moduleName": "default-geo-sections-geomasstype-editor",
            "moduleTitle": "Mass Type",
            "moduleStateSlotName": "default-geo-sections-geomasstype-editor"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-geo-sections-geomasstype-editor',
            () => import('../lib/ui/modules/DataEditor.js'),
            () => import('./modules/geo-sections/models/GeoMassTypeModel.js'),
        ),
    },
    {
        name: 'default-geo-sections-consistencytype-browser',
        path: '/default/geo-sections/consistencytype',
        privileges: [["adminSystem"]],
        options: {
            "entityName": "geo-sections.ConsistencyType",
            "name": "default-geo-sections-consistencytype-browser",
            "moduleName": "default-geo-sections-consistencytype-browser",
            "moduleTitle": "Consistency Types",
            "moduleStateSlotName": "default-geo-sections-consistencytype-browser"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-geo-sections-consistencytype-browser',
            () => import('../lib/ui/modules/DataBrowser.js'),
            () => import('./modules/geo-sections/models/ConsistencyTypeModel.js'),
        ),
    },
    {
        name: 'default-geo-sections-consistencytype-editor',
        path: '/default/geo-sections/consistencytype/:id',
        privileges: [["adminSystem"]],
        options: {
            "entityName": "geo-sections.ConsistencyType",
            "name": "default-geo-sections-consistencytype-editor",
            "moduleName": "default-geo-sections-consistencytype-editor",
            "moduleTitle": "Consistency Type",
            "moduleStateSlotName": "default-geo-sections-consistencytype-editor"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-geo-sections-consistencytype-editor',
            () => import('../lib/ui/modules/DataEditor.js'),
            () => import('./modules/geo-sections/models/ConsistencyTypeModel.js'),
        ),
    },
    {
        name: 'default-geo-sections-relativedensitytype-browser',
        path: '/default/geo-sections/relativedensitytype',
        privileges: [["adminSystem"]],
        options: {
            "entityName": "geo-sections.RelativeDensityType",
            "name": "default-geo-sections-relativedensitytype-browser",
            "moduleName": "default-geo-sections-relativedensitytype-browser",
            "moduleTitle": "Relative Density Types",
            "moduleStateSlotName": "default-geo-sections-relativedensitytype-browser"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-geo-sections-relativedensitytype-browser',
            () => import('../lib/ui/modules/DataBrowser.js'),
            () => import('./modules/geo-sections/models/RelativeDensityTypeModel.js'),
        ),
    },
    {
        name: 'default-geo-sections-relativedensitytype-editor',
        path: '/default/geo-sections/relativedensitytype/:id',
        privileges: [["adminSystem"]],
        options: {
            "entityName": "geo-sections.RelativeDensityType",
            "name": "default-geo-sections-relativedensitytype-editor",
            "moduleName": "default-geo-sections-relativedensitytype-editor",
            "moduleTitle": "Relative Density Type",
            "moduleStateSlotName": "default-geo-sections-relativedensitytype-editor"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-geo-sections-relativedensitytype-editor',
            () => import('../lib/ui/modules/DataEditor.js'),
            () => import('./modules/geo-sections/models/RelativeDensityTypeModel.js'),
        ),
    },
    {
        name: 'default-geo-sections-ucstype-browser',
        path: '/default/geo-sections/ucstype',
        privileges: [["adminSystem"]],
        options: {
            "entityName": "geo-sections.UcsType",
            "name": "default-geo-sections-ucstype-browser",
            "moduleName": "default-geo-sections-ucstype-browser",
            "moduleTitle": "Ucs Types",
            "moduleStateSlotName": "default-geo-sections-ucstype-browser"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-geo-sections-ucstype-browser',
            () => import('../lib/ui/modules/DataBrowser.js'),
            () => import('./modules/geo-sections/models/UcsTypeModel.js'),
        ),
    },
    {
        name: 'default-geo-sections-ucstype-editor',
        path: '/default/geo-sections/ucstype/:id',
        privileges: [["adminSystem"]],
        options: {
            "entityName": "geo-sections.UcsType",
            "name": "default-geo-sections-ucstype-editor",
            "moduleName": "default-geo-sections-ucstype-editor",
            "moduleTitle": "Ucs Type",
            "moduleStateSlotName": "default-geo-sections-ucstype-editor"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-geo-sections-ucstype-editor',
            () => import('../lib/ui/modules/DataEditor.js'),
            () => import('./modules/geo-sections/models/UcsTypeModel.js'),
        ),
    },
    {
        name: 'default-geo-sections-tunnelorientationtype-browser',
        path: '/default/geo-sections/tunnelorientationtype',
        privileges: [["adminSystem"]],
        options: {
            "entityName": "geo-sections.TunnelOrientationType",
            "name": "default-geo-sections-tunnelorientationtype-browser",
            "moduleName": "default-geo-sections-tunnelorientationtype-browser",
            "moduleTitle": "Tunnel Orientation Types",
            "moduleStateSlotName": "default-geo-sections-tunnelorientationtype-browser"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-geo-sections-tunnelorientationtype-browser',
            () => import('../lib/ui/modules/DataBrowser.js'),
            () => import('./modules/geo-sections/models/TunnelOrientationTypeModel.js'),
        ),
    },
    {
        name: 'default-geo-sections-tunnelorientationtype-editor',
        path: '/default/geo-sections/tunnelorientationtype/:id',
        privileges: [["adminSystem"]],
        options: {
            "entityName": "geo-sections.TunnelOrientationType",
            "name": "default-geo-sections-tunnelorientationtype-editor",
            "moduleName": "default-geo-sections-tunnelorientationtype-editor",
            "moduleTitle": "Tunnel Orientation Type",
            "moduleStateSlotName": "default-geo-sections-tunnelorientationtype-editor"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-geo-sections-tunnelorientationtype-editor',
            () => import('../lib/ui/modules/DataEditor.js'),
            () => import('./modules/geo-sections/models/TunnelOrientationTypeModel.js'),
        ),
    },
    {
        name: 'default-geo-sections-geosection-browser',
        path: '/default/geo-sections/geosection',
        privileges: [["adminGeotechData"],["manageGeotechData"]],
        options: {
            "entityName": "geo-sections.GeoSection",
            "name": "default-geo-sections-geosection-browser",
            "moduleName": "default-geo-sections-geosection-browser",
            "moduleTitle": "Geo Sections",
            "moduleStateSlotName": "default-geo-sections-geosection-browser"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-geo-sections-geosection-browser',
            () => import('../lib/ui/modules/DataBrowser.js'),
            () => import('./modules/geo-sections/models/GeoSectionModel.js'),
        ),
    },
    {
        name: 'default-geo-sections-geosection-editor',
        path: '/default/geo-sections/geosection/:id',
        privileges: [["adminGeotechData"],["manageGeotechData"]],
        options: {
            "entityName": "geo-sections.GeoSection",
            "name": "default-geo-sections-geosection-editor",
            "moduleName": "default-geo-sections-geosection-editor",
            "moduleTitle": "Geo Section",
            "moduleStateSlotName": "default-geo-sections-geosection-editor"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-geo-sections-geosection-editor',
            () => import('../lib/ui/modules/DataEditor.js'),
            () => import('./modules/geo-sections/models/GeoSectionModel.js'),
        ),
    },
    {
        name: 'default-geo-sections-geoparams-browser',
        path: '/default/geo-sections/geoparams',
        privileges: null,
        options: {
            "entityName": "geo-sections.GeoParams",
            "name": "default-geo-sections-geoparams-browser",
            "moduleName": "default-geo-sections-geoparams-browser",
            "moduleTitle": "Geo Params",
            "moduleStateSlotName": "default-geo-sections-geoparams-browser"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-geo-sections-geoparams-browser',
            () => import('../lib/ui/modules/DataBrowser.js'),
            () => import('./modules/geo-sections/models/GeoParamsModel.js'),
        ),
    },
    {
        name: 'default-geo-sections-geoparams-editor',
        path: '/default/geo-sections/geoparams/:id',
        privileges: null,
        options: {
            "entityName": "geo-sections.GeoParams",
            "name": "default-geo-sections-geoparams-editor",
            "moduleName": "default-geo-sections-geoparams-editor",
            "moduleTitle": "Geo Param",
            "moduleStateSlotName": "default-geo-sections-geoparams-editor"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-geo-sections-geoparams-editor',
            () => import('../lib/ui/modules/DataEditor.js'),
            () => import('./modules/geo-sections/models/GeoParamsModel.js'),
        ),
    },
    {
        name: 'default-geo-sections-geotype-browser',
        path: '/default/geo-sections/geotype',
        privileges: [["adminGeotechData"],["manageGeotechData"]],
        options: {
            "entityName": "geo-sections.GeoType",
            "name": "default-geo-sections-geotype-browser",
            "moduleName": "default-geo-sections-geotype-browser",
            "moduleTitle": "Geological Types",
            "moduleStateSlotName": "default-geo-sections-geotype-browser"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-geo-sections-geotype-browser',
            () => import('../lib/ui/modules/DataBrowser.js'),
            () => import('./modules/geo-sections/models/GeoTypeModel.js'),
        ),
    },
    {
        name: 'default-geo-sections-geotype-editor',
        path: '/default/geo-sections/geotype/:id',
        privileges: [["adminGeotechData"],["manageGeotechData"]],
        options: {
            "entityName": "geo-sections.GeoType",
            "name": "default-geo-sections-geotype-editor",
            "moduleName": "default-geo-sections-geotype-editor",
            "moduleTitle": "Geological Type",
            "moduleStateSlotName": "default-geo-sections-geotype-editor"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-geo-sections-geotype-editor',
            () => import('../lib/ui/modules/DataEditor.js'),
            () => import('./modules/geo-sections/models/GeoTypeModel.js'),
        ),
    },
    {
        name: 'default-geo-sections-geolayer-browser',
        path: '/default/geo-sections/geolayer',
        privileges: [["adminGeotechData"],["manageGeotechData"]],
        options: {
            "entityName": "geo-sections.GeoLayer",
            "name": "default-geo-sections-geolayer-browser",
            "moduleName": "default-geo-sections-geolayer-browser",
            "moduleTitle": "Geological Layers",
            "moduleStateSlotName": "default-geo-sections-geolayer-browser"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-geo-sections-geolayer-browser',
            () => import('../lib/ui/modules/DataBrowser.js'),
            () => import('./modules/geo-sections/models/GeoLayerModel.js'),
        ),
    },
    {
        name: 'default-geo-sections-geolayer-editor',
        path: '/default/geo-sections/geolayer/:id',
        privileges: [["adminGeotechData"],["manageGeotechData"]],
        options: {
            "entityName": "geo-sections.GeoLayer",
            "name": "default-geo-sections-geolayer-editor",
            "moduleName": "default-geo-sections-geolayer-editor",
            "moduleTitle": "Geological Layer",
            "moduleStateSlotName": "default-geo-sections-geolayer-editor"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-geo-sections-geolayer-editor',
            () => import('../lib/ui/modules/DataEditor.js'),
            () => import('./modules/geo-sections/models/GeoLayerModel.js'),
        ),
    },
    {
        name: 'default-geo-sections-waterbodytype-browser',
        path: '/default/geo-sections/waterbodytype',
        privileges: null,
        options: {
            "entityName": "geo-sections.WaterBodyType",
            "name": "default-geo-sections-waterbodytype-browser",
            "moduleName": "default-geo-sections-waterbodytype-browser",
            "moduleTitle": "Water Body Types",
            "moduleStateSlotName": "default-geo-sections-waterbodytype-browser"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-geo-sections-waterbodytype-browser',
            () => import('../lib/ui/modules/DataBrowser.js'),
            () => import('./modules/geo-sections/models/WaterBodyTypeModel.js'),
        ),
    },
    {
        name: 'default-geo-sections-waterbodytype-editor',
        path: '/default/geo-sections/waterbodytype/:id',
        privileges: null,
        options: {
            "entityName": "geo-sections.WaterBodyType",
            "name": "default-geo-sections-waterbodytype-editor",
            "moduleName": "default-geo-sections-waterbodytype-editor",
            "moduleTitle": "Water Body Type",
            "moduleStateSlotName": "default-geo-sections-waterbodytype-editor"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-geo-sections-waterbodytype-editor',
            () => import('../lib/ui/modules/DataEditor.js'),
            () => import('./modules/geo-sections/models/WaterBodyTypeModel.js'),
        ),
    },
    {
        name: 'default-geo-sections-waterbody-browser',
        path: '/default/geo-sections/waterbody',
        privileges: [["adminGeotechData"],["manageGeotechData"]],
        options: {
            "entityName": "geo-sections.WaterBody",
            "name": "default-geo-sections-waterbody-browser",
            "moduleName": "default-geo-sections-waterbody-browser",
            "moduleTitle": "Water Bodies",
            "moduleStateSlotName": "default-geo-sections-waterbody-browser"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-geo-sections-waterbody-browser',
            () => import('../lib/ui/modules/DataBrowser.js'),
            () => import('./modules/geo-sections/models/WaterBodyModel.js'),
        ),
    },
    {
        name: 'default-geo-sections-waterbody-editor',
        path: '/default/geo-sections/waterbody/:id',
        privileges: [["adminGeotechData"],["manageGeotechData"]],
        options: {
            "entityName": "geo-sections.WaterBody",
            "name": "default-geo-sections-waterbody-editor",
            "moduleName": "default-geo-sections-waterbody-editor",
            "moduleTitle": "Water Body",
            "moduleStateSlotName": "default-geo-sections-waterbody-editor"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-geo-sections-waterbody-editor',
            () => import('../lib/ui/modules/DataEditor.js'),
            () => import('./modules/geo-sections/models/WaterBodyModel.js'),
        ),
    },
    {
        name: 'default-earth-pressures-earthpressurescalculation-browser',
        path: '/default/earth-pressures/earthpressurescalculation',
        privileges: [["adminGeotechData"],["manageGeotechData"]],
        options: {
            "entityName": "earth-pressures.EarthPressuresCalculation",
            "name": "default-earth-pressures-earthpressurescalculation-browser",
            "moduleName": "default-earth-pressures-earthpressurescalculation-browser",
            "moduleTitle": "Ps Calculations",
            "moduleStateSlotName": "default-earth-pressures-earthpressurescalculation-browser"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-earth-pressures-earthpressurescalculation-browser',
            () => import('../lib/ui/modules/DataBrowser.js'),
            () => import('./modules/earth-pressures/models/EarthPressuresCalculationModel.js'),
        ),
    },
    {
        name: 'default-earth-pressures-earthpressurescalculation-editor',
        path: '/default/earth-pressures/earthpressurescalculation/:id',
        privileges: [["adminGeotechData"],["manageGeotechData"]],
        options: {
            "entityName": "earth-pressures.EarthPressuresCalculation",
            "name": "default-earth-pressures-earthpressurescalculation-editor",
            "moduleName": "default-earth-pressures-earthpressurescalculation-editor",
            "moduleTitle": "Ps Calculation",
            "moduleStateSlotName": "default-earth-pressures-earthpressurescalculation-editor"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-earth-pressures-earthpressurescalculation-editor',
            () => import('../lib/ui/modules/DataEditor.js'),
            () => import('./modules/earth-pressures/models/EarthPressuresCalculationModel.js'),
        ),
    },
    {
        name: 'default-earth-pressures-earthpressuresmodelinterval-browser',
        path: '/default/earth-pressures/earthpressuresmodelinterval',
        privileges: [["adminSystem"]],
        options: {
            "entityName": "earth-pressures.EarthPressuresModelInterval",
            "name": "default-earth-pressures-earthpressuresmodelinterval-browser",
            "moduleName": "default-earth-pressures-earthpressuresmodelinterval-browser",
            "moduleTitle": "Ps Model Intervals",
            "moduleStateSlotName": "default-earth-pressures-earthpressuresmodelinterval-browser"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-earth-pressures-earthpressuresmodelinterval-browser',
            () => import('../lib/ui/modules/DataBrowser.js'),
            () => import('./modules/earth-pressures/models/EarthPressuresModelIntervalModel.js'),
        ),
    },
    {
        name: 'default-earth-pressures-earthpressuresmodelinterval-editor',
        path: '/default/earth-pressures/earthpressuresmodelinterval/:id',
        privileges: [["adminSystem"]],
        options: {
            "entityName": "earth-pressures.EarthPressuresModelInterval",
            "name": "default-earth-pressures-earthpressuresmodelinterval-editor",
            "moduleName": "default-earth-pressures-earthpressuresmodelinterval-editor",
            "moduleTitle": "Ps Model Interval",
            "moduleStateSlotName": "default-earth-pressures-earthpressuresmodelinterval-editor"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-earth-pressures-earthpressuresmodelinterval-editor',
            () => import('../lib/ui/modules/DataEditor.js'),
            () => import('./modules/earth-pressures/models/EarthPressuresModelIntervalModel.js'),
        ),
    },
    {
        name: 'default-earth-pressures-earthpressuresdata-browser',
        path: '/default/earth-pressures/earthpressuresdata',
        privileges: null,
        options: {
            "entityName": "earth-pressures.EarthPressuresData",
            "name": "default-earth-pressures-earthpressuresdata-browser",
            "moduleName": "default-earth-pressures-earthpressuresdata-browser",
            "moduleTitle": "Primary Stress Results",
            "moduleStateSlotName": "default-earth-pressures-earthpressuresdata-browser"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-earth-pressures-earthpressuresdata-browser',
            () => import('../lib/ui/modules/DataBrowser.js'),
            () => import('./modules/earth-pressures/models/EarthPressuresDataModel.js'),
        ),
    },
    {
        name: 'default-earth-pressures-earthpressuresdata-editor',
        path: '/default/earth-pressures/earthpressuresdata/:id',
        privileges: null,
        options: {
            "entityName": "earth-pressures.EarthPressuresData",
            "name": "default-earth-pressures-earthpressuresdata-editor",
            "moduleName": "default-earth-pressures-earthpressuresdata-editor",
            "moduleTitle": "Primary Stress Result",
            "moduleStateSlotName": "default-earth-pressures-earthpressuresdata-editor"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-earth-pressures-earthpressuresdata-editor',
            () => import('../lib/ui/modules/DataEditor.js'),
            () => import('./modules/earth-pressures/models/EarthPressuresDataModel.js'),
        ),
    },
    {
        name: 'default-face-pressures-kacalculationmethod-browser',
        path: '/default/face-pressures/kacalculationmethod',
        privileges: null,
        options: {
            "entityName": "face-pressures.KaCalculationMethod",
            "name": "default-face-pressures-kacalculationmethod-browser",
            "moduleName": "default-face-pressures-kacalculationmethod-browser",
            "moduleTitle": "Ka Methods",
            "moduleStateSlotName": "default-face-pressures-kacalculationmethod-browser"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-face-pressures-kacalculationmethod-browser',
            () => import('../lib/ui/modules/DataBrowser.js'),
            () => import('./modules/face-pressures/models/KaCalculationMethodModel.js'),
        ),
    },
    {
        name: 'default-face-pressures-kacalculationmethod-editor',
        path: '/default/face-pressures/kacalculationmethod/:id',
        privileges: null,
        options: {
            "entityName": "face-pressures.KaCalculationMethod",
            "name": "default-face-pressures-kacalculationmethod-editor",
            "moduleName": "default-face-pressures-kacalculationmethod-editor",
            "moduleTitle": "Ka Method",
            "moduleStateSlotName": "default-face-pressures-kacalculationmethod-editor"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-face-pressures-kacalculationmethod-editor',
            () => import('../lib/ui/modules/DataEditor.js'),
            () => import('./modules/face-pressures/models/KaCalculationMethodModel.js'),
        ),
    },
    {
        name: 'default-face-pressures-k1calculationmethod-browser',
        path: '/default/face-pressures/k1calculationmethod',
        privileges: null,
        options: {
            "entityName": "face-pressures.K1CalculationMethod",
            "name": "default-face-pressures-k1calculationmethod-browser",
            "moduleName": "default-face-pressures-k1calculationmethod-browser",
            "moduleTitle": "K 1 Methods",
            "moduleStateSlotName": "default-face-pressures-k1calculationmethod-browser"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-face-pressures-k1calculationmethod-browser',
            () => import('../lib/ui/modules/DataBrowser.js'),
            () => import('./modules/face-pressures/models/K1CalculationMethodModel.js'),
        ),
    },
    {
        name: 'default-face-pressures-k1calculationmethod-editor',
        path: '/default/face-pressures/k1calculationmethod/:id',
        privileges: null,
        options: {
            "entityName": "face-pressures.K1CalculationMethod",
            "name": "default-face-pressures-k1calculationmethod-editor",
            "moduleName": "default-face-pressures-k1calculationmethod-editor",
            "moduleTitle": "K 1 Method",
            "moduleStateSlotName": "default-face-pressures-k1calculationmethod-editor"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-face-pressures-k1calculationmethod-editor',
            () => import('../lib/ui/modules/DataEditor.js'),
            () => import('./modules/face-pressures/models/K1CalculationMethodModel.js'),
        ),
    },
    {
        name: 'default-face-pressures-k2calculationmethod-browser',
        path: '/default/face-pressures/k2calculationmethod',
        privileges: null,
        options: {
            "entityName": "face-pressures.K2CalculationMethod",
            "name": "default-face-pressures-k2calculationmethod-browser",
            "moduleName": "default-face-pressures-k2calculationmethod-browser",
            "moduleTitle": "K 2 Methods",
            "moduleStateSlotName": "default-face-pressures-k2calculationmethod-browser"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-face-pressures-k2calculationmethod-browser',
            () => import('../lib/ui/modules/DataBrowser.js'),
            () => import('./modules/face-pressures/models/K2CalculationMethodModel.js'),
        ),
    },
    {
        name: 'default-face-pressures-k2calculationmethod-editor',
        path: '/default/face-pressures/k2calculationmethod/:id',
        privileges: null,
        options: {
            "entityName": "face-pressures.K2CalculationMethod",
            "name": "default-face-pressures-k2calculationmethod-editor",
            "moduleName": "default-face-pressures-k2calculationmethod-editor",
            "moduleTitle": "K 2 Method",
            "moduleStateSlotName": "default-face-pressures-k2calculationmethod-editor"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-face-pressures-k2calculationmethod-editor',
            () => import('../lib/ui/modules/DataEditor.js'),
            () => import('./modules/face-pressures/models/K2CalculationMethodModel.js'),
        ),
    },
    {
        name: 'default-face-pressures-trcalculationmethod-browser',
        path: '/default/face-pressures/trcalculationmethod',
        privileges: null,
        options: {
            "entityName": "face-pressures.TrCalculationMethod",
            "name": "default-face-pressures-trcalculationmethod-browser",
            "moduleName": "default-face-pressures-trcalculationmethod-browser",
            "moduleTitle": "Tr Methods",
            "moduleStateSlotName": "default-face-pressures-trcalculationmethod-browser"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-face-pressures-trcalculationmethod-browser',
            () => import('../lib/ui/modules/DataBrowser.js'),
            () => import('./modules/face-pressures/models/TrCalculationMethodModel.js'),
        ),
    },
    {
        name: 'default-face-pressures-trcalculationmethod-editor',
        path: '/default/face-pressures/trcalculationmethod/:id',
        privileges: null,
        options: {
            "entityName": "face-pressures.TrCalculationMethod",
            "name": "default-face-pressures-trcalculationmethod-editor",
            "moduleName": "default-face-pressures-trcalculationmethod-editor",
            "moduleTitle": "Tr Method",
            "moduleStateSlotName": "default-face-pressures-trcalculationmethod-editor"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-face-pressures-trcalculationmethod-editor',
            () => import('../lib/ui/modules/DataEditor.js'),
            () => import('./modules/face-pressures/models/TrCalculationMethodModel.js'),
        ),
    },
    {
        name: 'default-face-pressures-facepressurescalculation-browser',
        path: '/default/face-pressures/facepressurescalculation',
        privileges: [["adminGeotechData"],["manageGeotechData"]],
        options: {
            "entityName": "face-pressures.FacePressuresCalculation",
            "name": "default-face-pressures-facepressurescalculation-browser",
            "moduleName": "default-face-pressures-facepressurescalculation-browser",
            "moduleTitle": "J S Calculations",
            "moduleStateSlotName": "default-face-pressures-facepressurescalculation-browser"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-face-pressures-facepressurescalculation-browser',
            () => import('../lib/ui/modules/DataBrowser.js'),
            () => import('./modules/face-pressures/models/FacePressuresCalculationModel.js'),
        ),
    },
    {
        name: 'default-face-pressures-facepressurescalculation-editor',
        path: '/default/face-pressures/facepressurescalculation/:id',
        privileges: [["adminGeotechData"],["manageGeotechData"]],
        options: {
            "entityName": "face-pressures.FacePressuresCalculation",
            "name": "default-face-pressures-facepressurescalculation-editor",
            "moduleName": "default-face-pressures-facepressurescalculation-editor",
            "moduleTitle": "J S Calculation",
            "moduleStateSlotName": "default-face-pressures-facepressurescalculation-editor"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-face-pressures-facepressurescalculation-editor',
            () => import('../lib/ui/modules/DataEditor.js'),
            () => import('./modules/face-pressures/models/FacePressuresCalculationModel.js'),
        ),
    },
    {
        name: 'default-face-pressures-facepressuresmodelinterval-browser',
        path: '/default/face-pressures/facepressuresmodelinterval',
        privileges: null,
        options: {
            "entityName": "face-pressures.FacePressuresModelInterval",
            "name": "default-face-pressures-facepressuresmodelinterval-browser",
            "moduleName": "default-face-pressures-facepressuresmodelinterval-browser",
            "moduleTitle": "J S Model Intervals",
            "moduleStateSlotName": "default-face-pressures-facepressuresmodelinterval-browser"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-face-pressures-facepressuresmodelinterval-browser',
            () => import('../lib/ui/modules/DataBrowser.js'),
            () => import('./modules/face-pressures/models/FacePressuresModelIntervalModel.js'),
        ),
    },
    {
        name: 'default-face-pressures-facepressuresmodelinterval-editor',
        path: '/default/face-pressures/facepressuresmodelinterval/:id',
        privileges: null,
        options: {
            "entityName": "face-pressures.FacePressuresModelInterval",
            "name": "default-face-pressures-facepressuresmodelinterval-editor",
            "moduleName": "default-face-pressures-facepressuresmodelinterval-editor",
            "moduleTitle": "J S Model Interval",
            "moduleStateSlotName": "default-face-pressures-facepressuresmodelinterval-editor"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-face-pressures-facepressuresmodelinterval-editor',
            () => import('../lib/ui/modules/DataEditor.js'),
            () => import('./modules/face-pressures/models/FacePressuresModelIntervalModel.js'),
        ),
    },
    {
        name: 'default-face-pressures-facepressuresgeotechdata-browser',
        path: '/default/face-pressures/facepressuresgeotechdata',
        privileges: null,
        options: {
            "entityName": "face-pressures.FacePressuresGeotechData",
            "name": "default-face-pressures-facepressuresgeotechdata-browser",
            "moduleName": "default-face-pressures-facepressuresgeotechdata-browser",
            "moduleTitle": "J S Results",
            "moduleStateSlotName": "default-face-pressures-facepressuresgeotechdata-browser"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-face-pressures-facepressuresgeotechdata-browser',
            () => import('../lib/ui/modules/DataBrowser.js'),
            () => import('./modules/face-pressures/models/FacePressuresGeotechDataModel.js'),
        ),
    },
    {
        name: 'default-face-pressures-facepressuresgeotechdata-editor',
        path: '/default/face-pressures/facepressuresgeotechdata/:id',
        privileges: null,
        options: {
            "entityName": "face-pressures.FacePressuresGeotechData",
            "name": "default-face-pressures-facepressuresgeotechdata-editor",
            "moduleName": "default-face-pressures-facepressuresgeotechdata-editor",
            "moduleTitle": "J S Result",
            "moduleStateSlotName": "default-face-pressures-facepressuresgeotechdata-editor"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-face-pressures-facepressuresgeotechdata-editor',
            () => import('../lib/ui/modules/DataEditor.js'),
            () => import('./modules/face-pressures/models/FacePressuresGeotechDataModel.js'),
        ),
    },
    {
        name: 'default-face-pressures-facepressuresforcesdata-browser',
        path: '/default/face-pressures/facepressuresforcesdata',
        privileges: null,
        options: {
            "entityName": "face-pressures.FacePressuresForcesData",
            "name": "default-face-pressures-facepressuresforcesdata-browser",
            "moduleName": "default-face-pressures-facepressuresforcesdata-browser",
            "moduleTitle": "J S Eremax Results",
            "moduleStateSlotName": "default-face-pressures-facepressuresforcesdata-browser"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-face-pressures-facepressuresforcesdata-browser',
            () => import('../lib/ui/modules/DataBrowser.js'),
            () => import('./modules/face-pressures/models/FacePressuresForcesDataModel.js'),
        ),
    },
    {
        name: 'default-face-pressures-facepressuresforcesdata-editor',
        path: '/default/face-pressures/facepressuresforcesdata/:id',
        privileges: null,
        options: {
            "entityName": "face-pressures.FacePressuresForcesData",
            "name": "default-face-pressures-facepressuresforcesdata-editor",
            "moduleName": "default-face-pressures-facepressuresforcesdata-editor",
            "moduleTitle": "J S Eremax Result",
            "moduleStateSlotName": "default-face-pressures-facepressuresforcesdata-editor"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-face-pressures-facepressuresforcesdata-editor',
            () => import('../lib/ui/modules/DataEditor.js'),
            () => import('./modules/face-pressures/models/FacePressuresForcesDataModel.js'),
        ),
    },
    {
        name: 'default-face-pressures-facepressuresforcesatangle-browser',
        path: '/default/face-pressures/facepressuresforcesatangle',
        privileges: null,
        options: {
            "entityName": "face-pressures.FacePressuresForcesAtAngle",
            "name": "default-face-pressures-facepressuresforcesatangle-browser",
            "moduleName": "default-face-pressures-facepressuresforcesatangle-browser",
            "moduleTitle": "J S Zeta Results",
            "moduleStateSlotName": "default-face-pressures-facepressuresforcesatangle-browser"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-face-pressures-facepressuresforcesatangle-browser',
            () => import('../lib/ui/modules/DataBrowser.js'),
            () => import('./modules/face-pressures/models/FacePressuresForcesAtAngleModel.js'),
        ),
    },
    {
        name: 'default-face-pressures-facepressuresforcesatangle-editor',
        path: '/default/face-pressures/facepressuresforcesatangle/:id',
        privileges: null,
        options: {
            "entityName": "face-pressures.FacePressuresForcesAtAngle",
            "name": "default-face-pressures-facepressuresforcesatangle-editor",
            "moduleName": "default-face-pressures-facepressuresforcesatangle-editor",
            "moduleTitle": "J S Zeta Result",
            "moduleStateSlotName": "default-face-pressures-facepressuresforcesatangle-editor"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-face-pressures-facepressuresforcesatangle-editor',
            () => import('../lib/ui/modules/DataEditor.js'),
            () => import('./modules/face-pressures/models/FacePressuresForcesAtAngleModel.js'),
        ),
    },
    {
        name: 'default-face-pressures-facepressurespressuresatlevel-browser',
        path: '/default/face-pressures/facepressurespressuresatlevel',
        privileges: null,
        options: {
            "entityName": "face-pressures.FacePressuresPressuresAtLevel",
            "name": "default-face-pressures-facepressurespressuresatlevel-browser",
            "moduleName": "default-face-pressures-facepressurespressuresatlevel-browser",
            "moduleTitle": "J S Pressure Results",
            "moduleStateSlotName": "default-face-pressures-facepressurespressuresatlevel-browser"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-face-pressures-facepressurespressuresatlevel-browser',
            () => import('../lib/ui/modules/DataBrowser.js'),
            () => import('./modules/face-pressures/models/FacePressuresPressuresAtLevelModel.js'),
        ),
    },
    {
        name: 'default-face-pressures-facepressurespressuresatlevel-editor',
        path: '/default/face-pressures/facepressurespressuresatlevel/:id',
        privileges: null,
        options: {
            "entityName": "face-pressures.FacePressuresPressuresAtLevel",
            "name": "default-face-pressures-facepressurespressuresatlevel-editor",
            "moduleName": "default-face-pressures-facepressurespressuresatlevel-editor",
            "moduleTitle": "J S Pressure Result",
            "moduleStateSlotName": "default-face-pressures-facepressurespressuresatlevel-editor"
        },
        factory: async (app, routeDef, routeMatch) => createGeneratedRouteFactory(
            app,
            routeDef,
            'default-face-pressures-facepressurespressuresatlevel-editor',
            () => import('../lib/ui/modules/DataEditor.js'),
            () => import('./modules/face-pressures/models/FacePressuresPressuresAtLevelModel.js'),
        ),
    },
];
